import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate, useParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import 'react-datepicker/dist/react-datepicker.css';
import { sendEmail, briefNotificationEmail } from '../lib/email';
import { alertAdminsIfNonCompliantOnBriefAdd } from '../lib/complianceAlert';
import { useCurrentInternalUser } from '../lib/currentUser';
import Sidebar from '../components/Sidebar';
import { OttoLoading } from '../components/otto';
import MoneyInput from '../components/MoneyInput';
import SupplierBriefFactsPanel, { buildSupplierFacts } from '../components/SupplierBriefFactsPanel';
import TagChip from '../components/TagChip';
import TagFilterBar from '../components/TagFilterBar';
import { fetchTagsFor } from '../lib/tags';
import { fetchActivePortalUsersBySupplier, buildSelectedContactsFromPortalUsers } from '../lib/briefRecipients';
import { localTimezoneAbbr, defaultQuestionsClose, fmtDate } from '../lib/dates';
import { categoryLabel } from '../lib/supplierDisplay';
import { scrollBehavior } from '../lib/motion';
import ResurfacedActions from '../components/debrief/ResurfacedActions';
import BackIconButton from '../components/BackIconButton';
import ConfirmDialog from '../components/ConfirmDialog';
import '../lib/datepicker-theme.css';
import '../lib/quill-theme.css';
import { formatGBP } from '../lib/money';
import Alert from '../components/Alert';
import { quotezillaPrefill, handoffNote } from '../lib/quotezillaPrefill';

// Quill toolbar — keeping it tight on purpose. Bold/italic/underline plus
// ordered + unordered lists covers the formatting Fusion 2K has actually asked
// for; everything else is noise on a brief.
const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};
const quillFormats = ['bold', 'italic', 'underline', 'list'];

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024; // 10 MB per file
const ALLOWED_ATTACHMENT_EXTS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'];
const ALLOWED_ATTACHMENT_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/*';

const formatFileSize = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

// State stores datetime values as strings in datetime-local format
// ("YYYY-MM-DDTHH:mm") so writes to Supabase keep working unchanged.
// DatePicker works in Date objects, so we round-trip through these helpers.
const toDate = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};
const fromDate = (d) => {
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Date-order validation. Returns a { fieldName: errorMessage } map for
// any picker whose value contradicts a stronger date upstream. Empty
// object = clean form. Only checks the pairs that are actually in
// scope on this form (rig + de-rig gated by their "required" toggle;
// event live gated by the N/A toggles).
//
// Hard rules:
//   - rig end >= rig start
//   - de-rig end >= de-rig start
//   - event live end >= event live start
//   - event live start >= response deadline date (event happens after
//     suppliers have responded — operators can't pick June for the
//     event when the deadline is in August)
const detectDateOrderErrors = (f) => {
  const errors = {};
  const parse = (s) => {
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  };
  // Date-only floor of a datetime — used to compare a datetime
  // (response deadline) against a date (event live start) on the
  // same calendar-day basis. Returns midnight of the deadline's day.
  const dateFloor = (s) => {
    const d = parse(s);
    if (!d) return null;
    const floor = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    return floor;
  };
  // Rig + de-rig carry a time component (HH:mm), so a same-day-same-time
  // pair like "06 Sep 14:00 → 06 Sep 14:00" is a zero-length window —
  // not a valid rig schedule. Reject both inverted ranges (end<start)
  // AND zero-length ranges (end===start). Event Live below is date-only,
  // so end===start is a legitimate one-day event and stays allowed.
  if (f.rig_required) {
    const s = parse(f.rig_start_date);
    const e = parse(f.rig_end_date);
    if (s && e && e.getTime() <= s.getTime()) {
      errors.rig_end_date = 'Rig end must be after rig start.';
    }
  }
  if (f.derig_required) {
    const s = parse(f.derig_start_date);
    const e = parse(f.derig_end_date);
    if (s && e && e.getTime() <= s.getTime()) {
      errors.derig_end_date = 'De-rig end must be after De-rig start.';
    }
  }
  if (!f.event_live_date_na && !f.event_live_end_date_na) {
    const s = parse(f.event_live_date);
    const e = parse(f.event_live_end_date);
    if (s && e && e.getTime() < s.getTime()) {
      errors.event_live_end_date = 'Event Live End must be on or after Event Live Start.';
    }
  }
  // Cross-stage chronology — rig must finish before / at the event
  // start; de-rig must start at or after the event start (typically
  // at/after the event ends). Catches the "de-rig 07 Jun on a 12-13
  // Jun event" inversion that prompted the validation rule. Compared
  // on the floored start date so a same-day rig + event live is
  // permitted (rig finishes morning of, event runs in the afternoon).
  // Skips when event live is N/A (no chronology to check against).
  if (!f.event_live_date_na && f.event_live_date) {
    const eventStart = dateFloor(f.event_live_date);
    const eventEnd = f.event_live_end_date ? dateFloor(f.event_live_end_date) : eventStart;
    if (f.rig_required) {
      const rigStart = parse(f.rig_start_date);
      if (rigStart && eventEnd && rigStart.getTime() > eventEnd.getTime()) {
        errors.rig_start_date = 'Rig must start on or before the event ends.';
      }
    }
    if (f.derig_required) {
      const derigStart = parse(f.derig_start_date);
      if (derigStart && eventStart && derigStart.getTime() < eventStart.getTime()) {
        errors.derig_start_date = 'De-rig must start on or after the event begins.';
      }
    }
  }
  if (!f.event_live_date_na) {
    const startDay = dateFloor(f.event_live_date);
    const deadlineDay = dateFloor(f.required_response_time);
    if (startDay && deadlineDay && startDay.getTime() < deadlineDay.getTime()) {
      errors.event_live_date = 'Event Live Start must be on or after the Response Deadline.';
    }
  }
  return errors;
};

export default function AddBrief() {
  const navigate = useNavigate();
  const { id: editingBriefId } = useParams();
  const isEditMode = Boolean(editingBriefId);
  // Quotezilla sends people here with the job in the query string (see
  // lib/quotezillaPrefill). Read once, on a NEW brief only — an edit loads the
  // saved brief, and a stale link in the address bar must never overwrite it.
  const quotezillaHandoff = useMemo(
    () => (isEditMode ? null : quotezillaPrefill(window.location.search)),
    [isEditMode],
  );
  const { user: currentInternalUser } = useCurrentInternalUser();
  const [saving, setSaving] = useState(false);
  // Which path is currently saving — 'draft' or 'sent' — so the two
  // buttons can label their own in-flight state without the Send
  // button reading "Sending…" while a draft save is mid-write.
  const [savingMode, setSavingMode] = useState(null);
  // Flashes true for ~800ms after a successful draft save, before the
  // navigate fires. Drives the in-button "Saved ✓" confirmation so the
  // operator gets visible proof the draft persisted — without it the
  // page just navigated away and the user couldn't tell if the save
  // had actually committed.
  const [savedDraftFlash, setSavedDraftFlash] = useState(false);
  // Browser-local timezone abbreviation (e.g. "BST", "GMT", "EST"). Used
  // as a parenthetical on every datetime field so the staffer typing
  // 10:00 knows which clock that refers to. Stable for the lifetime of
  // the page — DST transitions during an editing session are a wild
  // edge case we don't need to dynamically respond to.
  const tzAbbr = localTimezoneAbbr();
  const [error, setError] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(isEditMode);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  // Exclusive-brief confirmation gate (2026-06): when the brief is
  // flagged Exclusive AND the operator tries to add a 2nd+ supplier,
  // toggleSupplier defers the add via this state and surfaces a
  // brand-styled ConfirmDialog instead of window.confirm.
  const [exclusiveConfirmSupplier, setExclusiveConfirmSupplier] = useState(null);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  // Supplier tags: per-supplier map (for chips), the vocabulary (for the
  // filter checkboxes), and the active tag filter (AND semantics).
  const [tagsBySupplier, setTagsBySupplier] = useState(new Map());
  const [recipientTagFilter, setRecipientTagFilter] = useState([]);
  // Map<supplier_id, [{id, full_name, email}]> of ACTIVE portal users — the
  // sole source for brief-comms recipients. Suppliers without any active
  // portal users are surfaced as unselectable in the picker and block the
  // Send button if somehow included (e.g. an existing selection where the
  // last portal user has just been disabled).
  const [portalUsersBySupplier, setPortalUsersBySupplier] = useState(new Map());
// Set of supplier_ids with a valid NDA on file (signed + future expiry +
  // admin-approved). When form.nda_required is true, suppliers outside this
  // set can't be added to the brief — both in the picker and on save.
  const [validNdaSupplierIds, setValidNdaSupplierIds] = useState(() => new Set());
  const [form, setForm] = useState({
    fusion_project_number: '',
    client: '',
    fusion_project_name: '',
    brief_name: '',
    brief_category: '',
    required_response_time: '',
    // When the question window closes (supplier-questions brief). Blank →
    // defaulted at save to 3 working days before the response deadline.
    questions_close: '',
    // DB column name kept as expected_required_by_date for backwards
    // compatibility; it now surfaces as the "Access from" date (the delivery
    // block was reframed as a site-access window).
    expected_required_by_date: '',
    // "Access to" — the end of the access window. Optional; empty means a
    // single-point access (just the "from"). New column delivery_access_to_date.
    delivery_access_to_date: '',
    // Tick to declare "this brief has no delivery date" deliberately
    // — distinct from leaving the picker empty (which reads as
    // "not yet set"). Clears expected_required_by_date on save.
    // Defaults to N/A (true) so the "Delivery Required" checkbox in
    // the UI — which renders as the inverse of this flag — starts
    // UNCHECKED. Most briefs don't involve a physical delivery, so
    // opt-in rather than opt-out is the right default.
    delivery_date_na: true,
    event_live_date: '',     // surfaces as "Event Live Start" in the UI
    event_live_end_date: '',
    // UI-only N/A toggles for the two event-live dates. Not persisted —
    // an empty date column on reload reads as "not set", and the user
    // can re-tick N/A if they want to express that deliberately.
    event_live_date_na: false,
    event_live_end_date_na: false,
    delivery_address: '',
    rig_required: false,
    rig_start_date: '',
    rig_end_date: '',
    rig_details: '',
    derig_required: false,
    derig_start_date: '',
    derig_end_date: '',
    derig_details: '',
    minimum_budget: '',
    maximum_budget: '',
    // Free-text event location/venue surfaced on the supplier brief
    // detail when show_location is true. New field — older briefs back-
    // fill to null.
    location: '',
    brief_content: '',
    is_privileged: false,
    nda_required: false,
    // Per-field supplier visibility flags. Defaults match the brief:
    // four shareable fields default visible, budget defaults hidden
    // (showing it can anchor supplier quotes). Staff toggle these in
    // the "What the supplier sees" panel further down the form.
    show_client: true,
    show_event_date: true,
    show_delivery_date: true,
    show_budget: false,
    show_location: true,
    // Newly-gated fields. Defaults preserve prior behaviour: project name
    // was always shown (TRUE); the rest were never shown (FALSE).
    show_project_name: true,
    show_project_number: false,
    show_category: false,
    show_delivery_address: false,
    show_rig: false,
    show_derig: false,
    // When true, a single reminder email fires 24h before
    // required_response_time to recipients who haven't responded.
    // Defaults false — opt-in per brief.
    auto_reminder_24h: false,
    // Optional brief type flag — chosen by Fusion's intent, not derived
    // from recipient count. NULL means "don't indicate" (default, matches
    // legacy behaviour). 'exclusive' / 'competitive' surface a pill +
    // context line on the supplier portal so the supplier knows whether
    // they're being asked solo or among many. Suppliers NEVER see a count.
    brief_type: null,
    status: 'draft',
    // A brief started from Quotezilla arrives with the job already in the link.
    // Last, so the hand-off wins over the blank defaults above — and only over
    // those: the fields it may set are whitelisted in quotezillaPrefill.
    ...(quotezillaHandoff?.values ?? {}),
  });
  // Multi-file attachments — supporting documents the supplier sees in the
  // portal once the brief is sent. Each item: { file_url, file_name,
  // file_size_bytes, mime_type, uploaded_at }. Uploads happen immediately
  // on file selection (so the supplier UX mirror sees the file before
  // submission); the brief_attachments rows are only inserted on save.
  const [attachments, setAttachments] = useState([]);
  // Amend-flow state: when editing an already-sent brief, the admin
  // can opt in to a "Notify recipients of this change" email fan-out
  // and add an optional change-message that surfaces in the email.
  // Defaults off — not every amend deserves a ping (typo fix, internal
  // notes tweak, etc.); the user actively ticks before clicking Save.
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [dragging, setDragging] = useState(false);
  // Path prefix in the brief-documents bucket. For an edit, we use the real
  // brief id. For a new brief, we generate a one-shot uuid so files uploaded
  // before save have a stable home; the brief_attachments row written on
  // save still references the real brief id (the storage path doesn't have
  // to match — the URL is the source of truth).
  const [draftUploadId] = useState(() =>
    isEditMode
      ? editingBriefId
      : (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2))
  );

  useEffect(() => {
    const getData = async () => {
      const { data: cats } = await supabase.from('list_values').select('value').eq('list_name', 'supplier_category').order('display_order');
      // Picker only offers active, non-on-hold, non-record-only suppliers.
      // Record-only rows exist for archive/reference only - no emails or
      // portal access fire to them, so they can't validly receive a brief.
      const { data: sups } = await supabase
        .from('suppliers')
        .select('id, supplier_name, category, main_contact_name, main_contact_email, secondary_contact_name, secondary_contact_email, on_hold, status')
        .eq('on_hold', false)
        .eq('status', 'active')
        .eq('is_record_only', false)
        .order('supplier_name');
      // Valid-NDA set. Considers a supplier_nda onboarding doc that's been
      // signed, has a confirmed future expiry, and isn't marked not_required.
      const { data: ndaRows } = await supabase
        .from('supplier_onboarding_documents')
        .select('supplier_id, signed_at, expiry_date, expiry_approved_at, not_required')
        .eq('document_key', 'supplier_nda');
      const now = new Date();
      const validIds = new Set();
      for (const row of ndaRows || []) {
        if (row.not_required) continue;
        if (!row.signed_at) continue;
        if (!row.expiry_date) continue;
        if (!row.expiry_approved_at) continue;
        const exp = new Date(row.expiry_date);
        if (isNaN(exp.getTime()) || exp <= now) continue;
        validIds.add(row.supplier_id);
      }
      setValidNdaSupplierIds(validIds);
      setCategories(cats || []);
      setSuppliers(sups || []);
      const supIds = (sups || []).map(s => s.id);
      fetchTagsFor('supplier', supIds).then(setTagsBySupplier);
      fetchActivePortalUsersBySupplier(supIds).then(setPortalUsersBySupplier);
    };
    getData();
  }, []);

  // Hydrate state on mount. In edit mode the database row wins (loads brief +
  // recipients). Otherwise hydrate any saved draft from localStorage so a tab
  // switch / re-render doesn't lose the user's work.
  useEffect(() => {
    if (isEditMode) {
      (async () => {
        const { data: brief } = await supabase.from('briefs').select('*').eq('id', editingBriefId).single();
        if (!brief) {
          setError('Brief not found');
          setLoadingExisting(false);
          return;
        }
        // Sent briefs no longer use this page — amendments live on the
        // brief detail's in-place AmendBriefPanel (versioned, with the
        // supplier reopen + amber markers). Hard-redirect anyone who lands
        // here so there's a single canonical amend flow.
        if (brief.status === 'sent') {
          navigate(`/briefs/${editingBriefId}`, { replace: true });
          return;
        }
        setForm(prev => ({
          ...prev,
          fusion_project_number: brief.fusion_project_number || '',
          client: brief.client || '',
          fusion_project_name: brief.fusion_project_name || '',
          brief_name: brief.brief_name || '',
          brief_category: brief.brief_category || '',
          required_response_time: brief.required_response_time || '',
          questions_close: brief.questions_close || '',
          expected_required_by_date: brief.expected_required_by_date || '',
          delivery_access_to_date: brief.delivery_access_to_date || '',
          delivery_date_na: brief.delivery_date_na || false,
          event_live_date: brief.event_live_date || '',
          event_live_end_date: brief.event_live_end_date || '',
          delivery_address: brief.delivery_address || '',
          rig_required: brief.rig_required || false,
          rig_start_date: brief.rig_start_date || '',
          rig_end_date: brief.rig_end_date || '',
          rig_details: brief.rig_details || '',
          derig_required: brief.derig_required || false,
          derig_start_date: brief.derig_start_date || '',
          derig_end_date: brief.derig_end_date || '',
          derig_details: brief.derig_details || '',
          minimum_budget: brief.minimum_budget ?? '',
          maximum_budget: brief.maximum_budget ?? '',
          location: brief.location || '',
          brief_content: brief.brief_content || '',
          is_privileged: brief.is_privileged || false,
          nda_required: brief.nda_required || false,
          // ?? rather than || here — explicit false from the DB must
          // survive (a staff member who turned a flag off shouldn't
          // see it flip back to true on re-edit).
          show_client:           brief.show_client           ?? true,
          show_event_date:       brief.show_event_date       ?? true,
          show_delivery_date:    brief.show_delivery_date    ?? true,
          show_budget:           brief.show_budget           ?? false,
          show_location:         brief.show_location         ?? true,
          show_project_name:     brief.show_project_name     ?? true,
          show_project_number:   brief.show_project_number   ?? false,
          show_category:         brief.show_category         ?? false,
          show_delivery_address: brief.show_delivery_address ?? false,
          show_rig:              brief.show_rig              ?? false,
          show_derig:            brief.show_derig            ?? false,
          auto_reminder_24h:   brief.auto_reminder_24h   ?? false,
          brief_type: brief.brief_type ?? null,
          status: brief.status || 'draft',
        }));
        const { data: recipients } = await supabase
          .from('brief_recipients')
          .select('*, suppliers(id, supplier_name, category)')
          .eq('brief_id', editingBriefId);
        // Keep the existing snapshot verbatim — operators can re-sync to
        // current portal users from the brief detail (legacy banner) if
        // needed. The snapshot is the single source of truth at edit time
        // so we never silently re-add a portal user the operator excluded.
        const mapped = (recipients || []).map(r => ({
          id: r.suppliers?.id || r.supplier_id,
          supplier_name: r.suppliers?.supplier_name || '',
          category: r.suppliers?.category || '',
          contacts: Array.isArray(r.selected_contacts) ? r.selected_contacts : [],
          additional_emails: r.additional_emails || '',
          response_deadline: r.response_deadline || '',
          status: r.status || 'pending',
        }));
        // Dedupe by supplier id. brief_recipients can hold duplicate rows for the
        // same supplier from an earlier save flow that swallowed delete errors —
        // without this, each duplicate renders its own tile and Remove appears
        // not to work. The next save's delete-then-insert cleans the DB.
        const deduped = Array.from(new Map(mapped.map(s => [s.id, s])).values());
        setSelectedSuppliers(deduped);
        const { data: atts } = await supabase
          .from('brief_attachments')
          .select('*')
          .eq('brief_id', editingBriefId)
          .order('uploaded_at', { ascending: true });
        if (atts && atts.length > 0) {
          setAttachments(atts.map(a => ({
            id: a.id,
            file_url: a.file_url,
            file_name: a.file_name,
            display_name: a.display_name || '',
            file_size_bytes: a.file_size_bytes,
            mime_type: a.mime_type,
            uploaded_at: a.uploaded_at,
            persisted: true,
          })));
        }
        setLoadingExisting(false);
      })();
    }
    // New-brief mode no longer hydrates from localStorage. The previous
    // "draft auto-save" persisted every field on type and surfaced as
    // mysterious pre-filled fields when users came back later after
    // navigating away without saving. Save as Draft / Send Brief /
    // Cancel are now the only ways the form state moves.
  }, [isEditMode, editingBriefId, navigate]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Hard date-order validation. Recomputed whenever form changes; gates
  // both the Save buttons and surfaces inline error messages beneath the
  // offending end-of-pair pickers.
  const dateOrderErrors = useMemo(() => detectDateOrderErrors(form), [form]);
  const hasDateOrderErrors = Object.keys(dateOrderErrors).length > 0;

  // ── What's blocking Send (round-2 §3/§4) ────────────────────────────────
  // ONE derivation feeds the tab badges, the "Can't send yet" panel and the
  // Send handler, so the three can never disagree about what's outstanding —
  // the failure mode that had Send looking ready on a brief with no
  // recipients at all.
  //
  // Each problem names the TAB it lives on and the field to focus, which is
  // what lets Send jump straight to the offending control instead of leaving
  // someone to hunt across four tabs.
  //
  // Deliberately unchanged: these are the rules that already governed Send.
  // The one ADDITION is the zero-recipients check — a brief sent to nobody was
  // previously accepted and quietly did nothing.
  // (The derivation itself lives just below `unsendableSelected`, which it
  // depends on.)
  const [showProblems, setShowProblems] = useState(false); // only after a Send attempt

  // Calendar-day floor of the response deadline — used as the minDate
  // for the Event Live Start / End pickers so the picker UI can't
  // open on a month earlier than the deadline. validateDateOrders is
  // the backstop for direct typing into the input.
  const responseDeadlineDay = useMemo(() => {
    if (!form.required_response_time) return null;
    const d = new Date(form.required_response_time);
    if (isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }, [form.required_response_time]);

  // Multi-file attachment upload — mirrors the SupplierResponse Phase B
  // pattern. Each file uploads immediately on selection / drop so the user
  // sees confirmation before saving the brief. Per-file validation: bad
  // files are reported in a combined error line but don't block the rest
  // of the batch.
  const handleFilesSelected = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploadingDoc(true);
    setError(null);
    const files = Array.from(fileList);
    const newAtt = [];
    const errors = [];
    for (const file of files) {
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (!ALLOWED_ATTACHMENT_EXTS.includes(ext)) {
        errors.push(`${file.name}: unsupported file type`);
        continue;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        errors.push(`${file.name}: too large (${(file.size / 1024 / 1024).toFixed(1)} MB, max 10 MB)`);
        continue;
      }
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      // Path matches the pattern used elsewhere in the bucket — a top-level
      // uuid folder for each brief (real brief id in edit mode, a client-
      // generated draft uuid for new briefs). Adding any other prefix
      // breaks the storage policy and surfaces as "Bucket not found".
      const path = `${draftUploadId}/${Date.now()}-${safeName}`;
      const { error: uploadError } = await supabase.storage
        .from('brief-documents')
        .upload(path, file, { contentType: file.type, upsert: false });
      if (uploadError) {
        errors.push(`${file.name}: upload failed (${uploadError.message})`);
        continue;
      }
      const { data: pub } = supabase.storage.from('brief-documents').getPublicUrl(path);
      newAtt.push({
        file_url: pub?.publicUrl || '',
        file_name: file.name,
        display_name: '',
        file_size_bytes: file.size,
        mime_type: file.type || null,
        uploaded_at: new Date().toISOString(),
      });
    }
    if (newAtt.length > 0) setAttachments(prev => [...prev, ...newAtt]);
    if (errors.length > 0) {
      setError(errors.join(' · '));
      setTimeout(() => setError(null), 10000);
    }
    setUploadingDoc(false);
  };
  const removeAttachment = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));
  // Inline-update one field on one attachment row (used by the
  // display-name input on each uploaded file).
  const updateAttachment = (idx, patch) => setAttachments(prev => prev.map((a, i) => i === idx ? { ...a, ...patch } : a));
  const handleDragOver = (e) => { e.preventDefault(); if (!uploadingDoc) setDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (uploadingDoc) return;
    handleFilesSelected(e.dataTransfer.files);
  };

  const sendBriefNotification = async (toEmail, toName, briefName, deadline, briefType) => {
    const { subject, html } = briefNotificationEmail({
      contactName: toName,
      briefName,
      deadline,
      appUrl: window.location.origin,
      briefType: briefType || null,
    });
    // sendEmail throws on failure; the brief save itself has already
    // committed, so we log + continue rather than unwinding the create
    // flow when one supplier's email bounces.
    try {
      await sendEmail({ to: toEmail, subject, html });
    } catch (err) {
      console.warn('briefNotificationEmail failed', { to: toEmail, error: err?.message });
    }
  };

  const toggleSupplier = (supplier, { skipExclusiveCheck = false } = {}) => {
    // NDA gate: when the brief requires an NDA, a supplier without a
    // valid NDA on file can't be added. De-selecting them is fine.
    const isAlreadySelected = selectedSuppliers.some(s => s.id === supplier.id);
    if (!isAlreadySelected && form.nda_required && !validNdaSupplierIds.has(supplier.id)) {
      setError(`${supplier.supplier_name} doesn't have a valid NDA on file (signed + approved future expiry). They can't be added to an NDA-required brief.`);
      setTimeout(() => setError(null), 8000);
      return;
    }
    // Exclusive-brief warning (2026-06): if the brief is flagged
    // Exclusive, the convention is that it goes to ONE supplier
    // only. Adding a second supplier breaks that convention — defer
    // the add to a brand-styled ConfirmDialog (state-driven). The
    // dialog only fires when ADDING; the toggle-off path is fine
    // without a prompt. skipExclusiveCheck=true bypasses this (used
    // by the dialog's Confirm handler to actually do the add after
    // the operator has acknowledged the exclusive flag).
    if (!skipExclusiveCheck && !isAlreadySelected && form.brief_type === 'exclusive' && selectedSuppliers.length >= 1) {
      setExclusiveConfirmSupplier(supplier);
      return;
    }
    // Block-send rule: a supplier with no active portal users can't receive
    // brief emails, so they can't be included on the brief. Selection is
    // refused at the picker; the unsendable chip on the row makes the
    // reason visible.
    const portalUsers = portalUsersBySupplier.get(supplier.id) || [];
    setSelectedSuppliers(prev => {
      const exists = prev.find(s => s.id === supplier.id);
      if (exists) return prev.filter(s => s.id !== supplier.id);
      if (portalUsers.length === 0) {
        setError(`${supplier.supplier_name} has no active portal user — add one on the supplier page before inviting them to a brief.`);
        setTimeout(() => setError(null), 6000);
        return prev;
      }
      return [...prev, {
        id: supplier.id,
        supplier_name: supplier.supplier_name,
        category: supplier.category,
        contacts: buildSelectedContactsFromPortalUsers(portalUsers, { selectedByDefault: true }),
        additional_emails: '',
        response_deadline: form.required_response_time || '',
        status: 'pending',
      }];
    });
  };

  // Used by the Remove button on already-selected supplier tiles. Direct removal
  // (no toggle fall-through) so a mismatched id can never accidentally re-add
  // the supplier as the toggle path would.
  const removeSelectedSupplier = (supplierId) => {
    setSelectedSuppliers(prev => prev.filter(s => s.id !== supplierId));
  };

  const toggleContact = (supplierId, contactEmail) => {
    setSelectedSuppliers(prev => prev.map(s => {
      if (s.id !== supplierId) return s;
      return { ...s, contacts: s.contacts.map(c => c.email === contactEmail ? { ...c, selected: !c.selected } : c) };
    }));
  };

  // Hard-stop guard for the Send button — refuses to send if any selected
  // supplier has zero selected portal-user contacts. Covers (a) a supplier
  // whose last portal user was disabled between selection and send, and
  // (b) an operator who unchecked every checkbox in the tile.
  const unsendableSelected = selectedSuppliers.filter(s => {
    const liveUsers = portalUsersBySupplier.get(s.id) || [];
    const selectedCount = (s.contacts || []).filter(c => c.selected && c.email).length;
    return liveUsers.length === 0 || selectedCount === 0;
  });

  // ── What's blocking Send (round-2 §3/§4) ────────────────────────────────
  // ONE derivation feeds the tab badges, the "Can't send yet" panel and the
  // Send handler, so the three can never disagree about what's outstanding —
  // which is how Send came to look ready on a brief with no recipients at all.
  //
  // Each problem names the TAB it lives on and the control to focus, which is
  // what lets Send jump straight to the offending field instead of leaving
  // someone to hunt across four tabs.
  //
  // The rules are the ones that already governed Send, with ONE addition: a
  // brief with no recipients. That was previously accepted and then quietly
  // sent to nobody.
  const sendProblems = useMemo(() => {
    const out = [];
    if (!String(form.brief_name || '').trim()) {
      out.push({ tab: 'brief-information', focus: 'brief-name', message: 'The brief needs a name.' });
    }
    Object.entries(dateOrderErrors).forEach(([field, message]) => {
      out.push({ tab: 'brief-information', focus: `date-${field}`, message });
    });
    if (selectedSuppliers.length === 0) {
      out.push({ tab: 'recipients', focus: 'supplier-search', message: 'Pick at least one supplier to send this brief to.' });
    }
    if (unsendableSelected.length > 0) {
      const names = unsendableSelected.map(s => s.supplier_name).join(', ');
      out.push({
        tab: 'recipients',
        focus: 'supplier-search',
        message: `No active portal user selected for ${names}. Remove them, or add a portal user on the supplier page.`,
      });
    }
    return out;
  }, [form.brief_name, dateOrderErrors, selectedSuppliers.length, unsendableSelected]);

  const tabProblemCount = useMemo(() => {
    const counts = {};
    for (const p of sendProblems) counts[p.tab] = (counts[p.tab] || 0) + 1;
    return counts;
  }, [sendProblems]);

  const updateSupplierField = (supplierId, field, value) => {
    setSelectedSuppliers(prev => prev.map(s => s.id === supplierId ? { ...s, [field]: value } : s));
  };

  const filteredSuppliers = suppliers.filter(s => {
    const matchesSearch = s.supplier_name?.toLowerCase().includes(supplierSearch.toLowerCase()) ||
      s.category?.toLowerCase().includes(supplierSearch.toLowerCase());
    const matchesCategory = !categoryFilter || s.category === categoryFilter;
    // Tag filter — AND: the supplier must carry every selected tag.
    let matchesTags = true;
    if (recipientTagFilter.length > 0) {
      const ids = new Set((tagsBySupplier.get(s.id) || []).map(t => t.id));
      matchesTags = recipientTagFilter.every(id => ids.has(id));
    }
    return matchesSearch && matchesCategory && matchesTags;
  });

  const handleSave = async (status) => {
    // §3: Send never fails silently. Every blocking problem is already derived
    // in `sendProblems`; this reveals the tab badges, switches to the first tab
    // that has a problem and focuses the control, so the answer to "why won't
    // this send" is on screen and under the cursor rather than guessed at.
    if (status === 'sent' && sendProblems.length > 0) {
      setShowProblems(true);
      const first = sendProblems[0];
      goToTab(first.tab);
      setError(`Can't send yet — ${first.message}`);
      setTimeout(() => setError(null), 8000);
      // After the panel has rendered. Focus without scroll-jacking: the panel
      // already reset to the top, and stealing the scroll again fights it.
      setTimeout(() => {
        const el = document.getElementById(first.focus);
        if (el) el.focus({ preventScroll: false });
      }, 60);
      return;
    }
    if (!form.brief_name) { setError('Brief name is required'); return; }
    // Hard date-order guard. Belt-and-braces — the END pickers also
    // carry minDate constraints that prevent picking an earlier date
    // in the calendar, but a typed/pasted value or a later edit to the
    // START field can still leave the pair in an inconsistent state.
    //
    // SEND only. This used to block drafts too, which made a half-built
    // brief impossible to park: the cross-stage rules (rig vs event) fire
    // on dates the operator hasn't finished entering, and the operator
    // then couldn't save OR leave without losing the lot. Same reasoning
    // as the unsendable-supplier guard below — nothing goes out from a
    // draft, so the inconsistency is harmless until Send.
    if (status === 'sent' && hasDateOrderErrors) {
      const first = Object.values(dateOrderErrors)[0];
      setError(`Fix the date order before saving — ${first}`);
      setTimeout(() => setError(null), 6000);
      return;
    }
    // Block-send guard — refuses to fire the email fan-out if any included
    // supplier has lost its portal users or had every contact unchecked.
    // Drafts are exempt because no email goes out and the operator may want
    // to fix the supplier roster before sending.
    if (status === 'sent' && unsendableSelected.length > 0) {
      const names = unsendableSelected.map(s => s.supplier_name).join(', ');
      setError(`Can't send — these suppliers have no active portal user selected: ${names}. Remove them or add a portal user on the supplier page.`);
      setTimeout(() => setError(null), 8000);
      return;
    }
    if (status === 'sent' && !window.confirm(`Send "${form.brief_name}" to ${selectedSuppliers.length} supplier${selectedSuppliers.length === 1 ? '' : 's'}? Notification emails will go out immediately.`)) return;
    setSaving(true);
    setSavingMode(status === 'sent' ? 'sent' : 'draft');
    setError(null);

    // Persist the brief itself — INSERT for a new brief, UPDATE for
    // an existing one. Empty strings are rejected by Postgres for
    // both DATE and timestamptz columns, so all three date fields
    // get normalised to null before send. (The earlier "timestamptz
    // tolerates ''" comment was wrong — saving a draft without a
    // response deadline surfaced "invalid input syntax for type
    // timestamp with time zone".)
    const blankToNull = (v) => (typeof v === 'string' && v.trim() === '' ? null : v);
    // Strip UI-only flags before spreading into the DB payload —
    // these track checkbox state for the event-date N/A toggles but
    // have no corresponding column on the briefs table, so leaving
    // them in the spread surfaced as PostgREST "could not find the
    // 'event_live_date_na' column" save failures.
    const { event_live_date_na: _eldNa, event_live_end_date_na: _eledNa, ...persistedForm } = form;
    const payload = {
      ...persistedForm,
      required_response_time: blankToNull(form.required_response_time),
      // Questions close: use the staffer's value, else default to 3 working
      // days before the response deadline (null if there's no deadline yet).
      questions_close: blankToNull(form.questions_close) || defaultQuestionsClose(form.required_response_time),
      // Delivery N/A force-clears the date so the two columns stay
      // coherent. UI also disables the picker when N/A is ticked but
      // we belt-and-braces it here.
      expected_required_by_date: form.delivery_date_na ? null : blankToNull(form.expected_required_by_date),
      delivery_access_to_date: form.delivery_date_na ? null : blankToNull(form.delivery_access_to_date),
      event_live_date: form.event_live_date_na ? null : blankToNull(form.event_live_date),
      event_live_end_date: form.event_live_end_date_na ? null : blankToNull(form.event_live_end_date),
      // numeric column — Postgres rejects '' the same way it does
      // for the date columns above.
      minimum_budget: blankToNull(form.minimum_budget),
      maximum_budget: blankToNull(form.maximum_budget),
      // Rig + de-rig details AND dates are tied to their toggle. If
      // the toggle is off, drop whatever's been typed/picked so DB
      // state matches what the user actually committed to.
      rig_start_date: form.rig_required ? blankToNull(form.rig_start_date) : null,
      rig_end_date: form.rig_required ? blankToNull(form.rig_end_date) : null,
      rig_details: form.rig_required ? (form.rig_details || null) : null,
      derig_start_date: form.derig_required ? blankToNull(form.derig_start_date) : null,
      derig_end_date: form.derig_required ? blankToNull(form.derig_end_date) : null,
      derig_details: form.derig_required ? (form.derig_details || null) : null,
      // Delivery address is tied to the "delivery required" toggle the
      // same way rig_details is tied to rig_required — clear it when
      // the user said N/A so the DB row stays coherent.
      delivery_address: form.delivery_date_na ? null : blankToNull(form.delivery_address),
      // Normalise the new free-text location field — empty stays null
      // rather than '' for clean nullability checks downstream.
      location: blankToNull(form.location),
      // Brief type audit. This page only edits drafts (sent briefs route
      // to AmendBriefPanel where the audit is stamped properly per-amend
      // with a change-vs-prior check). For drafts, a coarse "if type is
      // set, stamp set_at + set_by; if not, clear them" is enough — the
      // brief hasn't been seen by any supplier yet, so over-attributing
      // the audit on a re-save has no downstream effect.
      brief_type: form.brief_type || null,
      brief_type_set_at: form.brief_type ? new Date().toISOString() : null,
      brief_type_set_by: form.brief_type ? (currentInternalUser?.id || null) : null,
    };
    let briefId = editingBriefId;
    if (isEditMode) {
      const { error: updateError } = await supabase
        .from('briefs')
        .update({ ...payload, status })
        .eq('id', editingBriefId);
      if (updateError) { setError(updateError.message); setSaving(false); setSavingMode(null); return; }
    } else {
      // New briefs inherit the system-wide auto-archive default at creation
      // (changing the system setting later doesn't retroactively flip briefs).
      const { data: archiveSetting } = await supabase
        .from('app_settings').select('value').eq('key', 'auto_archive_briefs_enabled').single();
      const { data: brief, error: briefError } = await supabase
        .from('briefs')
        .insert([{ ...payload, status, owner_id: currentInternalUser?.id || null, auto_archive_enabled: archiveSetting?.value !== 'false' }])
        .select()
        .single();
      if (briefError) { setError(briefError.message); setSaving(false); setSavingMode(null); return; }
      briefId = brief.id;
      // Mirror the creator into brief_owners so the multi-owner UI shows them
      // immediately. The (brief_id, user_id) PK keeps this idempotent.
      if (currentInternalUser?.id) {
        await supabase.from('brief_owners').upsert({
          brief_id: briefId,
          user_id: currentInternalUser.id,
          added_by: currentInternalUser.id,
        }, { onConflict: 'brief_id,user_id' });
      }
    }

    // In edit mode, wipe and reinsert recipients (the form has the authoritative
    // selection). Safe because drafts haven't been sent — no responses to lose.
    if (isEditMode) {
      const { error: deleteError } = await supabase
        .from('brief_recipients')
        .delete()
        .eq('brief_id', briefId);
      if (deleteError) {
        setError(`Could not update suppliers: ${deleteError.message}`);
        setSaving(false); setSavingMode(null);
        return;
      }
    }

    // Sync brief_attachments. Wipe-and-rewrite is simple and idempotent —
    // matches the pattern used in SupplierResponse for response attachments.
    // Files in storage stay regardless; only the row pointers churn.
    const { error: attDeleteError } = await supabase.from('brief_attachments').delete().eq('brief_id', briefId);
    if (attDeleteError) {
      setError(`Could not save attachments (delete): ${attDeleteError.message}. Has the 2026-05-brief-attachments migration been run?`);
      setSaving(false); setSavingMode(null);
      return;
    }
    if (attachments.length > 0) {
      const attRows = attachments.map(a => ({
        brief_id: briefId,
        file_url: a.file_url,
        file_name: a.file_name,
        display_name: a.display_name && a.display_name.trim() !== '' ? a.display_name.trim() : null,
        file_size_bytes: a.file_size_bytes,
        mime_type: a.mime_type,
      }));
      const { error: attInsertError } = await supabase.from('brief_attachments').insert(attRows);
      if (attInsertError) {
        setError(`Could not save attachments: ${attInsertError.message}. Has the 2026-05-brief-attachments migration been run?`);
        setSaving(false); setSavingMode(null);
        return;
      }
    }
    if (selectedSuppliers.length > 0) {
      // When the brief is being sent (not just saved as draft), stamp
      // notified_at on each recipient row at insert time so the brief
      // detail page reflects "notified" immediately. Previously this
      // flow only emailed and never touched notified_at, so suppliers
      // received the brief but their recipient rows kept saying
      // NOT NOTIFIED forever — fixable only by clicking the per-row
      // Send button on the detail page, which did stamp notified_at.
      const sendAt = status === 'sent' ? new Date().toISOString() : null;
      const recipients = selectedSuppliers.map(s => ({
        brief_id: briefId,
        supplier_id: s.id,
        selected_contacts: s.contacts.filter(c => c.selected),
        additional_emails: s.additional_emails || null,
        response_deadline: s.response_deadline || null,
        status: 'pending',
        notified_at: sendAt,
      }));
      const { data: insertedRecipients } = await supabase
        .from('brief_recipients')
        .insert(recipients)
        .select('id, supplier_id');
      // Fan out compliance alerts for any amber/red supplier on this
      // brief. Fire-and-forget per supplier — the helper short-circuits
      // when the supplier is Green or no internal users have opted in.
      for (const sup of selectedSuppliers) {
        alertAdminsIfNonCompliantOnBriefAdd({
          supplierId: sup.id,
          briefId,
          addedByUserId: currentInternalUser?.id || null,
        });
      }

      if (status === 'sent') {
        // Log each initial send so the brief detail activity timeline
        // surfaces the event with kind='initial'. Mirrors the per-row
        // Send Brief flow at BriefDetail.handleSendDraft.
        const recipientIdBySupplier = new Map((insertedRecipients || []).map(r => [r.supplier_id, r.id]));
        const sendLogRows = [];
        for (const supplier of selectedSuppliers) {
          const deadline = supplier.response_deadline || form.required_response_time;
          // Strict portal-user routing — type='portal_user' only. New
          // briefs always populate the picker with portal-user-typed rows
          // via buildSelectedContactsFromPortalUsers; the filter belt-and-
          // braces a re-save scenario from ever silently hitting a
          // 'primary' / 'secondary' legacy entry.
          for (const contact of supplier.contacts.filter(c => c.selected && c.email && c.type === 'portal_user')) {
            await sendBriefNotification(contact.email, contact.name, form.brief_name, deadline, form.brief_type);
          }
          if (supplier.additional_emails) {
            const extras = supplier.additional_emails.split(';').map(e => e.trim()).filter(Boolean);
            for (const email of extras) {
              await sendBriefNotification(email, '', form.brief_name, deadline, form.brief_type);
            }
          }
          const recipientId = recipientIdBySupplier.get(supplier.id);
          if (recipientId) {
            sendLogRows.push({
              brief_id: briefId,
              recipient_id: recipientId,
              supplier_id: supplier.id,
              kind: 'initial',
              sent_at: sendAt,
              sent_by: currentInternalUser?.id || null,
            });
          }
        }
        if (sendLogRows.length > 0) {
          await supabase.from('brief_send_log').insert(sendLogRows);
        }
      }
    }
    // Sent briefs redirect out of this page on hydrate, so there's no
    // amend-from-edit fan-out here anymore — amendments live on the brief
    // detail's in-place AmendBriefPanel (versioned, with the supplier reopen
    // and amber markers).
    //
    // Draft saves flash a "Saved ✓" confirmation on the button for
    // ~800ms before navigating, so the operator sees the success state
    // rather than a silent navigate. The aria-live region (rendered
    // alongside the buttons) announces "Saved" for screen readers.
    if (status === 'draft') {
      setSavingMode(null);
      setSavedDraftFlash(true);
      setTimeout(() => {
        navigate(isEditMode ? `/briefs/${briefId}` : '/briefs');
      }, 800);
      return;
    }
    navigate(isEditMode ? `/briefs/${briefId}` : '/briefs');
  };

  const StatusBadge = ({ status }) => {
    if (status === 'responded') return (
      <span className="font-['Metropolis'] text-sm font-semibold px-3 py-1 rounded-xl border-2 border-[#92D400] text-fusion-forest">Accepted</span>
    );
    if (status === 'declined') return (
      <span className="font-['Metropolis'] text-sm font-semibold px-3 py-1 rounded-xl border-2 border-[#E0290A] text-[#E0290A]">Declined</span>
    );
    return null;
  };

  const tileStyle = (status) => {
    if (status === 'responded') return 'border-[#92D400]';
    if (status === 'declined') return 'border-[#E0290A]';
    return 'border-line';
  };

  const nameStyle = (status) => {
    if (status === 'responded') return 'text-fusion-forest';
    if (status === 'declined') return 'text-[#E0290A]';
    return 'text-ink';
  };

  // ── Tabs (round-2 §1) ───────────────────────────────────────────────────
  // These were SCROLL ANCHORS on one continuous page, styled as tabs. They
  // read as tabs, so people expected tab behaviour and got a jump to a scroll
  // offset instead — which meant every tab but the first opened part-way
  // through the previous tab's content ("The brief" landing mid-way down the
  // De-rig card, "Recipients" showing Rig/De-rig rows above Brief Type).
  //
  // They are now real tab panels: ONLY the active panel renders. Every field's
  // value lives in `form` at component scope, so unmounting a panel discards
  // markup, never data — switching away and back preserves everything typed.
  //
  // This also answers the earlier open question about a stepped wizard: tabs
  // give the same orientation without imposing an order on a form people fill
  // out of sequence.
  const TABS = [
    { id: 'brief-information', label: 'Brief information' },
    { id: 'the-brief',         label: 'The brief' },
    { id: 'supplier-view',     label: 'Supplier view' },
    { id: 'recipients',        label: 'Recipients' },
  ];
  const scrollerRef = useRef(null);
  const tabRefs = useRef({});
  const [activeTab, setActiveTab] = useState(TABS[0].id);

  // §1 second half: a tab must open at ITS top, not wherever the last one was
  // scrolled to. Without this the panel swaps under a scroll offset and the
  // new tab appears to open half way down.
  const goToTab = useCallback((tabId) => {
    setActiveTab(tabId);
    const root = scrollerRef.current;
    if (root) root.scrollTo({ top: 0, behavior: scrollBehavior() });
  }, []);

  // Arrow-key movement across the tab list, per the tablist pattern. Home/End
  // jump to the ends; the roving focus follows so the keyboard and the mouse
  // land in the same place.
  const onTabKeyDown = (e) => {
    const i = TABS.findIndex(t => t.id === activeTab);
    let next = null;
    if (e.key === 'ArrowRight') next = TABS[(i + 1) % TABS.length];
    else if (e.key === 'ArrowLeft') next = TABS[(i - 1 + TABS.length) % TABS.length];
    else if (e.key === 'Home') next = TABS[0];
    else if (e.key === 'End') next = TABS[TABS.length - 1];
    if (!next) return;
    e.preventDefault();
    goToTab(next.id);
    tabRefs.current[next.id]?.focus();
  };

  // ── Layout tokens (tidy-up §2/§3) ───────────────────────────────────────
  // ONE content container for the whole page. The header used to put its
  // padding INSIDE the max-width box while the form put the box inside the
  // padding, so the two ended up 64px apart on a wide monitor and no two
  // sections shared a left edge. Every section — header, form, footer — uses
  // this and nothing else sets its own horizontal width or gutter.
  const pageContainer = "w-full max-w-[1240px] mx-auto px-8";
  // Spacing scale, 4px base. Named by role so the relationship between them
  // survives editing: label→field is one step, field→field is five, and a
  // section break is eight.
  const labelGap = "mb-1";      //  4px
  const fieldGap = "gap-5";     // 20px
  // placeholder-[#5F6252] is deliberate: unset, placeholders fall back to the
  // browser default (~#757575, ~4.6:1 on white) which is borderline AA and
  // visibly weaker in the date + search fields. This clears 4.5:1 with room.
  // focus-visible ring is on the token so no field can be missed by keyboard.
  const inputClass = "w-full bg-white border border-line rounded-xl text-ink px-4 py-1.5 font-['Metropolis'] text-sm placeholder-[#5F6252] focus:outline-none focus:border-[#92D400] focus-visible:ring-2 focus-visible:ring-[#92D400]/60 transition-colors";
  const labelClass = `font-['Metropolis'] text-xs font-semibold text-[#51534A] block ${labelGap}`;
  const sectionClass = "font-['Metropolis'] text-[11px] font-semibold tracking-[0.18em] uppercase text-fusion-forest mb-4 mt-8 pb-2 border-b border-line";
  // Visibility pills (§9/§10). Sentence case, medium weight, no tracking —
  // caps are reserved for section headers and the page title, so pills stop
  // competing with them.
  //
  // FILL carries the meaning: a filled pill is a state YOU set (Shown/Hidden,
  // the checkbox rows); an outlined pill is one you can't change (Always
  // shown, Empty). Previously all four looked the same, so a locked state read
  // as a toggle you'd failed to notice. The four labels differ in wording too,
  // so the states never rest on colour alone.
  const pillBase = "flex-shrink-0 font-['Metropolis'] text-[10px] font-medium rounded-full px-2.5 py-0.5 border whitespace-nowrap";
  const PILL = {
    alwaysShown: `${pillBase} bg-transparent border-fusion-forest/40 text-fusion-forest`,
    empty: `${pillBase} bg-transparent border-line text-[#6B6E5F]`,
    shown: `${pillBase} bg-[#92D400]/20 border-[#92D400]/50 text-fusion-forest`,
    hidden: `${pillBase} bg-[#E89A2E]/20 border-[#E89A2E]/50 text-[#7A4E10]`,
  };
  // The form has two kinds of checkbox that looked identical but do very
  // different things: an inline "N/A" that CLEARS one field, and these, which
  // REVEAL a set of fields. The chevron is the tell — it says "this opens
  // more" before you click, which is the whole point of §3. Paired with
  // aria-expanded on the input so assistive tech gets the same signal.
  const revealChevron = (open) => (
    <svg
      width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      className={`text-label flex-shrink-0 ml-auto transition-transform duration-150 ${open ? 'rotate-180' : ''}`}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );

  if (loadingExisting) return (
    <div className="min-h-screen bg-canvas flex items-center justify-center">
      <OttoLoading size="large" message="Loading brief" />
    </div>
  );

  return (
    <div className="h-screen bg-canvas flex overflow-hidden">

      {/* Sidebar — sticky to viewport so it stays visible while the main
          content scrolls. */}
      <Sidebar currentInternalUser={currentInternalUser} />

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Page header — small breadcrumb-style back link, inline H1,
            actions on the right, divider beneath. Matches the Dashboard
            pattern; the divider is inset (mx-8) so it doesn't run
            edge-to-edge across the page. */}
        <div className="pt-8 flex-shrink-0">
          {/* Header shares the form's max-width so the title and actions line
              up with the fields beneath rather than drifting to the far edge
              of a wide monitor (Create Brief refactor §2). */}
          <div className={`${pageContainer} mb-3`}>
            <BackIconButton to="/briefs" label="Back to briefs" />
          </div>
          <div className={`${pageContainer} flex items-start justify-between gap-6 mb-6`}>
            <div className="min-w-0">
              <h1 className="fusion-h1 text-fusion-forest">{isEditMode ? 'Edit Brief' : 'Create New Brief'}</h1>
              <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mt-2">Set the brief, pick suppliers and send it out — or save as a draft and come back later.</p>
            </div>
            {/* Action stack (§1). Three tiers, and Cancel sits BELOW the two
                saves rather than beside them — it discards, so it shouldn't
                sit in the same row as the actions that keep your work. */}
            <div className="flex flex-col items-end gap-2 flex-shrink-0 pt-1">
              <div className="flex items-center gap-3 flex-wrap justify-end">
              {error && <p className="font-['Metropolis'] text-sm text-[#E0290A]">{error}</p>}
              {/* AddBrief is the draft editor only — sent briefs redirect
                  to the detail page above. Three buttons, matching the
                  dashboard's tiers exactly (Widgets / New Supplier / New
                  Brief): neutral outline, green outline, solid green.

                  §5: Cancel was underlined text hanging BELOW Send at the
                  right edge, which read as a stray link rather than an action.
                  It now sits in the row as the neutral-outline tier, leftmost,
                  because it's the one that discards. */}
              <button
                onClick={() => navigate('/briefs')}
                disabled={saving}
                // TERTIARY — text only, no border, no fill (round 3 §1).
                // Round 2 moved Cancel into this row and gave it a neutral
                // outline, which left three actions reading as two: Cancel and
                // Save as Draft were both white pills with a border, so the eye
                // saw a matched pair plus a primary rather than a ladder.
                // Cancel is the least likely thing anyone wants and the only
                // one that discards work — it should be findable, not
                // competing. Keeps a generous hit area and a visible focus
                // ring; only the box goes.
                className="rounded-xl font-['Metropolis'] text-sm font-semibold px-4 py-2 bg-transparent border-0 text-[#51534A] inline-flex items-center gap-2 transition-colors hover:text-ink disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest">
                Cancel
              </button>
              {/* Save Draft — secondary outline button. Hover fills
                  with brand-lime + flips text dark; active press
                  scales 0.97. The state machine runs Save → Saving…
                  (spinner) → Saved ✓ → navigate. aria-busy stamps
                  during the request; the aria-live region below
                  announces "Saved" to screen readers when the
                  confirmation renders. prefers-reduced-motion drops
                  the transforms via the .save-draft-btn class in
                  src/index.css (keeps the colour transitions for
                  responsiveness; cuts the scale + spin animations). */}
              <button
                onClick={() => handleSave('draft')}
                disabled={saving || savedDraftFlash}
                aria-busy={savingMode === 'draft'}
                // Secondary (§1): transparent fill, deep-green border at ~40%,
                // deep-green text. The old border was brand lime on white,
                // which barely read as a button edge at all.
                // Green-outline tier — the dashboard's "New Supplier" button.
                // White fill (not transparent) so it reads as a raised control
                // against the sage page the way the dashboard's does.
                className={`save-draft-btn group rounded-xl font-['Metropolis'] text-sm font-semibold px-5 py-2 border inline-flex items-center gap-2 transition-colors disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest ${
                  savedDraftFlash
                    ? 'bg-white border-fusion-forest text-fusion-forest'
                    : 'bg-white border-fusion-forest/50 text-fusion-forest hover:border-fusion-forest hover:bg-fusion-forest/[0.06] active:bg-fusion-forest/10 disabled:opacity-50'
                }`}
              >
                {savingMode === 'draft' && (
                  <svg className="save-draft-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" strokeOpacity="0.25" />
                    <path d="M21 12a9 9 0 0 0-9-9" strokeLinecap="round" />
                  </svg>
                )}
                {savedDraftFlash && (
                  <svg className="save-draft-tick" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
                <span>
                  {savingMode === 'draft'
                    ? 'Saving…'
                    : savedDraftFlash
                      ? 'Saved'
                      : (isEditMode ? 'Save Draft' : 'Save as Draft')}
                </span>
              </button>
              {/* Screen-reader-only live region — announces the saved
                  state once the flash kicks in so AT users get the
                  confirmation even though they can't see the tick. */}
              <span role="status" aria-live="polite" className="sr-only">
                {savedDraftFlash ? 'Draft saved.' : ''}
              </span>
              <button
                onClick={() => handleSave('sent')}
                // §3: NO LONGER DISABLED by validation. A disabled button
                // doesn't fire pointer events in Chrome, so the title below
                // never rendered — which is how Send came to sit there dead
                // with no explanation. It now always clicks, and clicking runs
                // validation, opens the failing tab and focuses the field.
                // Still disabled while a save is genuinely in flight.
                disabled={saving}
                // Solid-green tier — the dashboard's "New Brief" button.
                // Brand lime with near-black text (#131313 on #92D400 ≈ 9.9:1,
                // comfortably past AA). An earlier pass made this deep green
                // with white text for weight; Rob's call is that it matches the
                // dashboard, and consistency across the two surfaces wins.
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#92D400] text-[#131313] font-['Metropolis'] text-sm font-bold px-6 py-2.5 shadow-[0_8px_18px_-10px_rgba(80,120,0,0.75)] transition-colors hover:bg-[#a4e810] active:bg-[#7fb800] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none">
                {savingMode === 'sent' ? 'Sending…' : 'Send Brief'}
              </button>
              </div>
            </div>
          </div>
          {/* Why Send is disabled, stated on the page. A disabled button
                doesn't receive pointer events in Chrome, so the title
                attribute above never appears — which left a dead Send
                button with no explanation anywhere. Two of the six
                date-order rules (rig/de-rig start vs the event window)
                also had no inline message beside their picker, so the
                form could be unsendable with nothing on screen saying so.
                Each row jumps to the offending field. */}
          {showProblems && sendProblems.length > 0 && (
            <div className={`${pageContainer} mb-4`}>
              <div role="alert" className="rounded-xl border border-[#E0290A]/40 bg-[#E0290A]/[0.06] px-4 py-3">
                <p className="font-['Metropolis'] text-sm font-bold text-[#B01F06]">
                  Can't send yet — {sendProblems.length} thing{sendProblems.length === 1 ? '' : 's'} to fix
                </p>
                <ul className="mt-1.5 space-y-1">
                  {/* Now driven by the same `sendProblems` the tab badges and
                      the Send handler use, so the panel can't list a different
                      set of problems from the one blocking the button. Each row
                      switches to the right TAB before focusing — without that
                      the field it wants isn't in the DOM at all. */}
                  {sendProblems.map((p, i) => (
                    <li key={`${p.tab}-${p.focus}-${i}`}>
                      <button
                        type="button"
                        onClick={() => {
                          goToTab(p.tab);
                          setTimeout(() => {
                            const el = document.getElementById(p.focus)
                              || document.querySelector(`[data-date-error="${p.focus.replace(/^date-/, '')}"]`);
                            if (el) {
                              el.scrollIntoView({ block: 'center', behavior: scrollBehavior() });
                              if (typeof el.focus === 'function') el.focus({ preventScroll: true });
                            }
                          }, 60);
                        }}
                        className="text-left font-['Metropolis'] text-sm text-[#7A1B05] underline decoration-[#E0290A]/40 underline-offset-2 hover:decoration-[#E0290A] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E0290A] rounded">
                        {p.message}
                        <span className="text-[#9B4A34] no-underline"> · {TABS.find(t => t.id === p.tab)?.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
                <p className="mt-1.5 font-['Metropolis'] text-xs text-[#51534A]">
                  You can still Save as Draft — none of this goes out until you send.
                </p>
              </div>
            </div>
          )}
          {/* Divider tracks the content edges rather than the viewport, so it
              starts and ends exactly where the fields do. */}
          <div className={pageContainer}>
            <div className="h-px bg-[#E6E9DA]" aria-hidden="true" />
          </div>
          {/* Tab list (§1). Lives in the header block, which doesn't scroll,
              so it stays put without needing position:sticky. A real tablist:
              roving tabindex, arrow keys, aria-controls pointing at the panel.
              Scrolls horizontally rather than wrapping on a narrow screen, so
              the bar stays one line at 375px. */}
          <div
            role="tablist"
            aria-label="Brief form sections"
            onKeyDown={onTabKeyDown}
            // §4 — more air above, and NO rule beneath. The row sat between two
            // identical hairlines, which boxed it into a strip: it read as a
            // band of chrome rather than as the navigation for the panel
            // directly below it. Losing the lower rule lets the tabs belong to
            // their panel, and the extra top margin separates them from the
            // header they are not part of.
            className={`${pageContainer} mt-4 pt-1 pb-3 flex items-center gap-1 overflow-x-auto`}>
            {TABS.map(t => {
              const active = activeTab === t.id;
              const problems = showProblems ? (tabProblemCount[t.id] || 0) : 0;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  id={`tab-${t.id}`}
                  ref={el => { tabRefs.current[t.id] = el; }}
                  aria-selected={active}
                  aria-controls={`panel-${t.id}`}
                  tabIndex={active ? 0 : -1}
                  onClick={() => goToTab(t.id)}
                  className={`flex-shrink-0 font-['Metropolis'] text-xs font-semibold rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest ${
                    active
                      ? 'bg-fusion-forest text-white'
                      : 'text-[#51534A] hover:text-fusion-forest hover:bg-fusion-forest/[0.06]'
                  }`}>
                  {t.label}
                  {/* §4 completion indicator. Only after a send has been
                      attempted — flagging untouched fields on arrival would
                      make a blank form look broken. Carries the COUNT as text,
                      not a bare colour dot, so it survives greyscale and reads
                      the same to a screen reader. */}
                  {problems > 0 && (
                    <span
                      className={`inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full font-['Metropolis'] text-[10px] font-bold leading-none ${
                        active ? 'bg-white text-fusion-forest' : 'bg-[#E0290A] text-white'
                      }`}>
                      {problems}
                      <span className="sr-only"> {problems === 1 ? 'field still needed' : 'fields still needed'}</span>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {/* The rule that used to sit here is gone (§4) — see the tablist
              comment above. The active tab's own underline is the only edge
              the row needs. */}
        </div>

        {/* Form */}
        <div ref={scrollerRef} className="flex-1 overflow-y-auto py-8">
          {/* Width comes from pageContainer and nowhere else. Four blocks
              (Access, Install/De-rig, The Brief, Supporting Documents) used to
              carry their own 1040px cap, which is why those cards stopped
              short of the right edge while the Budget/Location grid ran to it
              — the drift in tidy-up §2. If a single control ever needs to be
              narrower than the container, cap that control, not its section. */}
          <div className={pageContainer}>

            {/* ══ PANEL 1 — Brief information ══════════════════════════════
                Project/client fields, dates, budget, location, and the
                Access / Rig / De-rig requirement blocks. */}
            {activeTab === 'brief-information' && (
            <section role="tabpanel" id="panel-brief-information" aria-labelledby="tab-brief-information" tabIndex={-1}>
            {/* §7: the tab already says "Brief information" — repeating it as a
                section heading directly beneath was the label twice. Genuine
                sub-headings inside a panel (Supporting Documents, Settings)
                stay. */}
            <p className="font-['Metropolis'] text-xs text-[#51534A] mb-4">Fields marked <span className="text-[#E0290A] font-semibold">*</span> are required.</p>

            {/* Open actions from earlier debriefs that look relevant to this
                project (project-debriefs §5). Setting up the next job for a
                client is the moment last time's lessons are worth having, so
                they appear here rather than as a reminder nobody reads.
                Suggestive only — it gates nothing. Renders nothing when the
                project number is empty or nothing matches. */}
            {/* Say where the details came from. Someone arriving from
                Quotezilla should see a form that is already filled in AND know
                why — an unexplained pre-filled form reads as a bug, and the
                figures still need checking before anything goes to a supplier. */}
            {quotezillaHandoff && (
              <Alert
                tone="blue"
                title="Started from Quotezilla"
                body={handoffNote(quotezillaHandoff.from)}
                className="mb-5" />
            )}

            <ResurfacedActions
              projectRef={form.fusion_project_number}
              client={form.client}
              className="mb-5" />

            <div className={`grid grid-cols-2 ${fieldGap}`}>
              <div>
                <label className={labelClass}>Project Number</label>
                <input className={inputClass} value={form.fusion_project_number} onChange={e => set('fusion_project_number', e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>Client</label>
                <input className={inputClass} value={form.client} onChange={e => set('client', e.target.value)} placeholder="e.g. Renault, Kia" />
              </div>
              <div>
                <label className={labelClass}>Project Name</label>
                <input className={inputClass} value={form.fusion_project_name} onChange={e => set('fusion_project_name', e.target.value)} />
              </div>
              <div>
                {/* Brief name is the ONLY genuinely required field on this
                    form — everything else saves happily blank (see the
                    validation at handleSave). The asterisk is decorative, so
                    it's hidden from assistive tech and paired with real text;
                    aria-required carries the meaning, and htmlFor/id ties the
                    label to the field so it's announced at all. Deliberately
                    NOT the native `required` attribute: this isn't a <form>
                    with onSubmit, so it would do nothing here except risk a
                    browser bubble competing with our own error banner. */}
                <label className={labelClass} htmlFor="brief-name">
                  Brief Name <span className="text-[#E0290A]" aria-hidden="true">*</span>
                  <span className="sr-only">(required)</span>
                </label>
                <input
                  id="brief-name"
                  aria-required="true"
                  className={inputClass}
                  value={form.brief_name}
                  onChange={e => set('brief_name', e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>Brief Category</label>
                <select className={inputClass} value={form.brief_category} onChange={e => { set('brief_category', e.target.value); setCategoryFilter(e.target.value); }}>
                  <option value="">Select category...</option>
                  {categories.map(c => <option key={c.value} value={c.value}>{c.value}</option>)}
                </select>
              </div>
              <div>
                {/* "Response Deadline" matches the terminology used on
                    the brief detail page and the supplier-facing facts
                    panel (was "Required Response Time" here only, which
                    drifted across surfaces). The TZ abbreviation tells
                    the staffer which clock 10:00 refers to — closes the
                    silent-disagreement loop that caused the earlier
                    rig-time mismatch between admin and supplier views. */}
                <label className={labelClass}>Response Deadline <span className="text-[#51534A]/80 font-normal normal-case tracking-normal">({tzAbbr})</span></label>
                <DatePicker
                  selected={toDate(form.required_response_time)}
                  onChange={(d) => set('required_response_time', fromDate(d))}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="dd/MM/yyyy HH:mm"
                  placeholderText="dd/mm/yyyy --:--"
                  isClearable
                  className={inputClass}
                  wrapperClassName="block w-full" />
                {/* 24h reminder opt-in — sits beneath the deadline
                    because it depends on it. When the deadline is
                    less than 24h away the checkbox can't schedule a
                    reminder so we disable it with an amber hint; if
                    staff later extend the deadline the box re-enables.
                    Send time line shows the exact moment the reminder
                    would fire (deadline - 24h) in British format. */}
                {(() => {
                  const MS_24H = 24 * 60 * 60 * 1000;
                  const deadline = form.required_response_time ? new Date(form.required_response_time) : null;
                  const msUntil = deadline ? deadline.getTime() - Date.now() : null;
                  const tooSoon = msUntil != null && msUntil < MS_24H;
                  const noDeadline = !deadline;
                  const disabled = noDeadline || tooSoon;

                  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                  const fireAt = deadline ? new Date(deadline.getTime() - MS_24H) : null;
                  const fireAtLabel = fireAt ? `${fireAt.getDate()} ${MONTHS[fireAt.getMonth()]} ${fireAt.getFullYear()}, ${String(fireAt.getHours()).padStart(2,'0')}:${String(fireAt.getMinutes()).padStart(2,'0')}` : null;

                  return (
                    <div className="mt-3">
                      <label className={`flex items-start gap-2.5 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={!!form.auto_reminder_24h && !disabled}
                          disabled={disabled}
                          onChange={e => set('auto_reminder_24h', e.target.checked)}
                          className="w-4 h-4 mt-0.5 accent-active-green flex-shrink-0 disabled:opacity-40" />
                        <div className="min-w-0">
                          <p className="font-['Metropolis'] text-xs font-semibold text-fusion-mid-grey">Send auto-reminder 24h before deadline</p>
                          <p className="font-['Metropolis'] text-[11px] text-[#51534A] leading-snug mt-0.5">
                            A single reminder email goes to any supplier who hasn't submitted yet, 24 hours before the deadline. Suppliers who've already submitted or declined are skipped.
                          </p>
                          {form.auto_reminder_24h && !disabled && fireAtLabel && (
                            <p className="font-['Metropolis'] text-[11px] text-fusion-forest mt-1">
                              Reminder will send: {fireAtLabel}
                            </p>
                          )}
                          {tooSoon && (
                            <p className="font-['Metropolis'] text-[11px] text-[#7A4E10] mt-1">
                              This deadline is less than 24 hours away, so a reminder can't be scheduled. Extend the deadline to enable reminders.
                            </p>
                          )}
                          {/* §10: the box is disabled in TWO cases but only
                              one of them said so. With no deadline set at all
                              it just sat there greyed out with no reason —
                              the more common of the two, since the deadline is
                              usually filled in after this point on the form. */}
                          {/* §11 — MUTED, not amber, and this deliberately
                              splits the pair the round-3 brief warned about
                              splitting. They were styled alike because they sit
                              together, but they are not the same kind of thing:
                                tooSoon   — the deadline you SET has a
                                            consequence you probably didn't
                                            intend. That is a warning and stays
                                            amber.
                                noDeadline— nothing is wrong. You just haven't
                                            filled a field in yet, on a form you
                                            are part-way through. Amber made an
                                            ordinary in-progress state look like
                                            a mistake.
                              Styling them identically was the fault; matching
                              them was never the goal. */}
                          {noDeadline && (
                            <p className="font-['Metropolis'] text-[11px] text-[#51534A] mt-1">
                              Set a response deadline above to enable reminders — the reminder fires 24 hours before it.
                            </p>
                          )}
                        </div>
                      </label>
                    </div>
                  );
                })()}
                {/* Questions close — the moment suppliers can no longer ask a
                    question about the brief. Defaults (on save) to 3 working
                    days before the response deadline; editable here. A question
                    an hour before close can't be answered fairly. */}
                <div className="mt-5">
                  <label className={labelClass}>Questions close <span className="text-[#51534A]/80 font-normal normal-case tracking-normal">({tzAbbr})</span></label>
                  <DatePicker
                    selected={toDate(form.questions_close)}
                    onChange={(d) => set('questions_close', fromDate(d))}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    dateFormat="dd/MM/yyyy HH:mm"
                    placeholderText="dd/mm/yyyy --:--"
                    isClearable
                    className={inputClass}
                    wrapperClassName="block w-full" />
                  <p className="font-['Metropolis'] text-[11px] text-[#51534A] leading-snug mt-1">
                    {!form.questions_close && form.required_response_time
                      ? `Leave blank to use the default — 3 working days before the response deadline (${fmtDate(defaultQuestionsClose(form.required_response_time))}).`
                      : 'After this, suppliers can no longer ask questions; existing threads stay answerable.'}
                  </p>
                </div>
              </div>
              {/* Event Live Start + End sit on the same row with their
                  own N/A toggles, mirroring the Delivery Date pattern.
                  The N/A flags are UI-only — not persisted — but they
                  clear the date column on save so the DB row stays
                  coherent. */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className={`${labelClass} mb-0`}>Event Live Start</label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.event_live_date_na}
                      onChange={e => {
                        const checked = e.target.checked;
                        set('event_live_date_na', checked);
                        if (checked) set('event_live_date', '');
                      }}
                      // Both N/A boxes read as bare "N/A" to a screen reader,
                      // which is ambiguous when two sit on the same row. Name
                      // the field each one clears.
                      aria-label="Event Live Start not applicable"
                      className="w-3.5 h-3.5 accent-active-green" />
                    <span className="font-['Metropolis'] text-xs text-[#51534A]">N/A</span>
                  </label>
                </div>
                <DatePicker
                  selected={form.event_live_date_na ? null : toDate(form.event_live_date)}
                  onChange={(d) => set('event_live_date', d
                    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
                    : '')}
                  disabled={form.event_live_date_na}
                  dateFormat="dd/MM/yyyy"
                  placeholderText={form.event_live_date_na ? 'Not applicable' : 'dd/mm/yyyy'}
                  isClearable
                  minDate={responseDeadlineDay}
                  className={`${inputClass} ${form.event_live_date_na ? 'opacity-50' : ''} ${dateOrderErrors.event_live_date ? 'border-[#E0290A]' : ''}`}
                  wrapperClassName="block w-full" />
                {dateOrderErrors.event_live_date && (
                  <p data-date-error="event_live_date" className="font-['Metropolis'] text-[11px] text-[#E0290A] mt-1">{dateOrderErrors.event_live_date}</p>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className={`${labelClass} mb-0`}>Event Live End</label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.event_live_end_date_na}
                      onChange={e => {
                        const checked = e.target.checked;
                        set('event_live_end_date_na', checked);
                        if (checked) set('event_live_end_date', '');
                      }}
                      aria-label="Event Live End not applicable"
                      className="w-3.5 h-3.5 accent-active-green" />
                    <span className="font-['Metropolis'] text-xs text-[#51534A]">N/A</span>
                  </label>
                </div>
                <DatePicker
                  selected={form.event_live_end_date_na ? null : toDate(form.event_live_end_date)}
                  onChange={(d) => set('event_live_end_date', d
                    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
                    : '')}
                  disabled={form.event_live_end_date_na}
                  dateFormat="dd/MM/yyyy"
                  placeholderText={form.event_live_end_date_na ? 'Not applicable' : 'dd/mm/yyyy'}
                  isClearable
                  minDate={(() => {
                    // Floor for the End picker is the later of the
                    // event-live start (if set) and the response
                    // deadline day — so even if no Start is picked
                    // yet, End can't sit before the deadline.
                    const startDate = form.event_live_date_na ? null : toDate(form.event_live_date);
                    if (startDate && responseDeadlineDay) {
                      return startDate.getTime() > responseDeadlineDay.getTime() ? startDate : responseDeadlineDay;
                    }
                    return startDate || responseDeadlineDay;
                  })()}
                  className={`${inputClass} ${form.event_live_end_date_na ? 'opacity-50' : ''} ${dateOrderErrors.event_live_end_date ? 'border-[#E0290A]' : ''}`}
                  wrapperClassName="block w-full" />
                {dateOrderErrors.event_live_end_date && (
                  <p data-date-error="event_live_end_date" className="font-['Metropolis'] text-[11px] text-[#E0290A] mt-1">{dateOrderErrors.event_live_end_date}</p>
                )}
              </div>
              <div>
                {/* Budget — supports a single ceiling (just Max) OR a
                    range (Min + Max). Both fields optional. Range
                    semantics:
                      Max only       → "Up to £N + VAT"  (legacy default)
                      Min only       → "£N+ + VAT"
                      Min AND Max    → "£N – £M + VAT"
                      Neither        → blank / "—"
                    Validation: when both are filled, Min must be ≤ Max
                    (live-checked below the inputs). */}
                <label className={labelClass}>Budget (if known)</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="font-['Metropolis'] text-[11px] text-[#51534A] mb-1">Min</p>
                    <MoneyInput value={form.minimum_budget} onChange={(v) => set('minimum_budget', v)} min="0" />
                  </div>
                  <div>
                    <p className="font-['Metropolis'] text-[11px] text-[#51534A] mb-1">Max</p>
                    <MoneyInput value={form.maximum_budget} onChange={(v) => set('maximum_budget', v)} min="0" />
                  </div>
                </div>
                {(() => {
                  const mn = Number(form.minimum_budget);
                  const mx = Number(form.maximum_budget);
                  const both = form.minimum_budget !== '' && form.maximum_budget !== '' && !Number.isNaN(mn) && !Number.isNaN(mx);
                  if (both && mn > mx) {
                    return <p className="font-['Metropolis'] text-[11px] text-[#E0290A] mt-1">Min must be at or below Max.</p>;
                  }
                  return null;
                })()}
              </div>
              <div>
                <label className={labelClass}>Location</label>
                <input className={inputClass} value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Silverstone, Northants" />
              </div>
            </div>

            {/* Delivery — rig-style expand. Ticking "Delivery Required"
                reveals the date + address fields; unticking collapses
                them and (on save) blanks the persisted columns so an
                accidental address can't survive the toggle going off.
                Storage-wise we keep the existing delivery_date_na flag
                as the inverse of "required" so no migration is needed. */}
            <div className="mt-6">
              <div className="bg-white border border-line rounded-xl p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!form.delivery_date_na}
                    onChange={e => {
                      const required = e.target.checked;
                      set('delivery_date_na', !required);
                      if (!required) {
                        set('expected_required_by_date', '');
                        set('delivery_access_to_date', '');
                        set('delivery_address', '');
                      }
                    }}
                    aria-expanded={!form.delivery_date_na}
                    className="w-4 h-4 accent-active-green" />
                  <div>
                    {/* §10: sentence case across all three requirement blocks —
                        they read "Access Required" / "Rig required" /
                        "De-rig Required" side by side. */}
                    <p className="font-['Metropolis'] text-sm text-fusion-mid-grey font-semibold">Access required</p>
                    <p className="font-['Metropolis'] text-xs text-[#51534A]">Tick if the supplier needs site access — opens the access dates and address fields.</p>
                  </div>
                  {revealChevron(!form.delivery_date_na)}
                </label>
                {!form.delivery_date_na && (
                  <div className="mt-4 space-y-3">
                    {/* Access window — "from" reuses the existing
                        expected_required_by_date column; "to" is the new
                        delivery_access_to_date. "to" is optional (single-point
                        access just sets "from"). */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className={labelClass}>Access from</label>
                        <DatePicker
                          selected={toDate(form.expected_required_by_date)}
                          onChange={(d) => set('expected_required_by_date', fromDate(d))}
                          showTimeSelect
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          dateFormat="dd/MM/yyyy HH:mm"
                          placeholderText="dd/mm/yyyy --:--"
                          isClearable
                          className={inputClass}
                          wrapperClassName="block w-full" />
                      </div>
                      <div>
                        <label className={labelClass}>Access to <span className="text-[#51534A] font-normal normal-case tracking-normal">(optional)</span></label>
                        <DatePicker
                          selected={toDate(form.delivery_access_to_date)}
                          onChange={(d) => set('delivery_access_to_date', fromDate(d))}
                          showTimeSelect
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          dateFormat="dd/MM/yyyy HH:mm"
                          placeholderText="dd/mm/yyyy --:--"
                          isClearable
                          className={inputClass}
                          wrapperClassName="block w-full" />
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>Access Address</label>
                      <textarea
                        value={form.delivery_address}
                        onChange={e => set('delivery_address', e.target.value)}
                        rows={3}
                        placeholder="Site address — include access notes / loading bay / contact-on-arrival if relevant"
                        className={`${inputClass} resize-y min-h-[88px]`} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Rig / De-rig — each is a toggle that reveals a free-text
                details box when ticked. The textareas persist their
                value across toggle flips in form state, but `handleSave`
                normalises details to null whenever the toggle is off
                so DB rows can't say "rig_required=false, rig_details=
                pages of text" by accident. */}
            <div className="mt-6 space-y-4">
              <div className="bg-white border border-line rounded-xl p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.rig_required}
                    onChange={e => set('rig_required', e.target.checked)}
                    aria-expanded={form.rig_required}
                    className="w-4 h-4 accent-active-green" />
                  <div>
                    <p className="font-['Metropolis'] text-sm text-fusion-mid-grey font-semibold">Rig required</p>
                    <p className="font-['Metropolis'] text-xs text-[#51534A]">Tick if the supplier needs to rig / set up on site.</p>
                  </div>
                  {revealChevron(form.rig_required)}
                </label>
                {form.rig_required && (
                  <div className="mt-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelClass}>Rig start <span className="text-[#51534A]/80 font-normal normal-case tracking-normal">({tzAbbr})</span></label>
                        <DatePicker
                          selected={toDate(form.rig_start_date)}
                          onChange={(d) => set('rig_start_date', fromDate(d))}
                          showTimeSelect
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          dateFormat="dd/MM/yyyy HH:mm"
                          placeholderText="dd/mm/yyyy --:--"
                          isClearable
                          className={`${inputClass} ${dateOrderErrors.rig_start_date ? 'border-[#E0290A]' : ''}`}
                          wrapperClassName="block w-full" />
                        {dateOrderErrors.rig_start_date && (
                          <p data-date-error="rig_start_date" className="font-['Metropolis'] text-[11px] text-[#E0290A] mt-1">{dateOrderErrors.rig_start_date}</p>
                        )}
                      </div>
                      <div>
                        <label className={labelClass}>Rig end <span className="text-[#51534A]/80 font-normal normal-case tracking-normal">({tzAbbr})</span></label>
                        <DatePicker
                          selected={toDate(form.rig_end_date)}
                          onChange={(d) => set('rig_end_date', fromDate(d))}
                          showTimeSelect
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          dateFormat="dd/MM/yyyy HH:mm"
                          placeholderText="dd/mm/yyyy --:--"
                          isClearable
                          minDate={toDate(form.rig_start_date)}
                          className={`${inputClass} ${dateOrderErrors.rig_end_date ? 'border-[#E0290A]' : ''}`}
                          wrapperClassName="block w-full" />
                        {dateOrderErrors.rig_end_date && (
                          <p data-date-error="rig_end_date" className="font-['Metropolis'] text-[11px] text-[#E0290A] mt-1">{dateOrderErrors.rig_end_date}</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>Rig details</label>
                      {/* Rich text (matches The Brief editor) so install notes
                          can use lists/emphasis — suppliers read these to quote.
                          Stored as HTML; every display site renders via the
                          looksLikeHtml() pattern so legacy plain-text rows still
                          show correctly. */}
                      <div className="sp-quill">
                        <ReactQuill
                          theme="snow"
                          value={form.rig_details}
                          onChange={(html) => set('rig_details', html)}
                          modules={quillModules}
                          formats={quillFormats}
                          placeholder="Access times, kit list, any client-side requirements the supplier needs to know before they quote" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="bg-white border border-line rounded-xl p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.derig_required}
                    onChange={e => set('derig_required', e.target.checked)}
                    aria-expanded={form.derig_required}
                    className="w-4 h-4 accent-active-green" />
                  <div>
                    <p className="font-['Metropolis'] text-sm text-fusion-mid-grey font-semibold">De-rig required</p>
                    <p className="font-['Metropolis'] text-xs text-[#51534A]">Tick if the supplier needs to tear down / collect after the event.</p>
                  </div>
                  {revealChevron(form.derig_required)}
                </label>
                {form.derig_required && (
                  <div className="mt-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelClass}>De-rig start <span className="text-[#51534A]/80 font-normal normal-case tracking-normal">({tzAbbr})</span></label>
                        <DatePicker
                          selected={toDate(form.derig_start_date)}
                          onChange={(d) => set('derig_start_date', fromDate(d))}
                          showTimeSelect
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          dateFormat="dd/MM/yyyy HH:mm"
                          placeholderText="dd/mm/yyyy --:--"
                          isClearable
                          className={`${inputClass} ${dateOrderErrors.derig_start_date ? 'border-[#E0290A]' : ''}`}
                          wrapperClassName="block w-full" />
                        {dateOrderErrors.derig_start_date && (
                          <p data-date-error="derig_start_date" className="font-['Metropolis'] text-[11px] text-[#E0290A] mt-1">{dateOrderErrors.derig_start_date}</p>
                        )}
                      </div>
                      <div>
                        <label className={labelClass}>De-rig end <span className="text-[#51534A]/80 font-normal normal-case tracking-normal">({tzAbbr})</span></label>
                        <DatePicker
                          selected={toDate(form.derig_end_date)}
                          onChange={(d) => set('derig_end_date', fromDate(d))}
                          showTimeSelect
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          dateFormat="dd/MM/yyyy HH:mm"
                          placeholderText="dd/mm/yyyy --:--"
                          isClearable
                          minDate={toDate(form.derig_start_date)}
                          className={`${inputClass} ${dateOrderErrors.derig_end_date ? 'border-[#E0290A]' : ''}`}
                          wrapperClassName="block w-full" />
                        {dateOrderErrors.derig_end_date && (
                          <p data-date-error="derig_end_date" className="font-['Metropolis'] text-[11px] text-[#E0290A] mt-1">{dateOrderErrors.derig_end_date}</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>De-rig details</label>
                      {/* Rich text — sibling of Install details; same rationale. */}
                      <div className="sp-quill">
                        <ReactQuill
                          theme="snow"
                          value={form.derig_details}
                          onChange={(html) => set('derig_details', html)}
                          modules={quillModules}
                          formats={quillFormats}
                          placeholder="Get-out window, collection access, where kit lands after the event" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            </section>
            )}

            {/* ══ PANEL 2 — The brief ══════════════════════════════════════
                The brief body, supporting documents, and the internal-only
                settings (Privileged, NDA). */}
            {activeTab === 'the-brief' && (
            <section role="tabpanel" id="panel-the-brief" aria-labelledby="tab-the-brief" tabIndex={-1}>
            <div>
              {/* §7 (round 2): no repeated "The Brief" heading — the tab names
                  the panel, and the heading stays removed.
                  §5 (round 3): but the panel opened on a bare formatting
                  toolbar, which tells you how to type and nothing about what to
                  type. ONE line, doing the job the heading wasn't: what this
                  text is for and who reads it. Not a heading, and not a second
                  set of instructions — the placeholder still handles "start
                  here". */}
              <p className="font-['Metropolis'] text-[12px] text-[#51534A] leading-snug mb-3 max-w-[70ch]">
                This is what suppliers read when they open the brief — the scope, the deliverables, and anything they need to price accurately.
              </p>
              <div className="sp-quill">
                <ReactQuill
                  theme="snow"
                  value={form.brief_content}
                  onChange={(html) => set('brief_content', html)}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Enter the full brief details here..." />
              </div>
            </div>

            {/* === Supporting documents (multi-file) ===
                Files uploaded here are visible to suppliers in the portal
                when the brief is sent, and to admins on the brief detail
                page. Mirrors the SupplierResponse dropzone pattern. */}
            <div className="mt-6">
              {/* §9: the accepted formats were listed here AND inside the
                  dropzone. The dropzone is where you look when you're about to
                  drop a file, so that's the copy that stays. */}
              <p className={sectionClass}>Supporting Documents <span className="text-[#51534A] font-normal normal-case tracking-normal">(optional)</span></p>
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`bg-white border-2 border-dashed rounded-xl p-6 transition-colors ${dragging ? 'border-[#92D400] bg-[#92D400]/5' : 'border-line'}`}
              >
                <div className="flex flex-col items-center justify-center text-center gap-2">
                  <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                    {dragging ? 'Drop to attach' : 'Drag and drop files here, or'}
                  </p>
                  {/* Demoted from bright lime: it was competing with Send
                      Brief for "the primary action" on the same screen, and
                      there is only ever one primary (§3/§4). */}
                  <label className="fusion-btn fusion-btn-secondary text-xs cursor-pointer whitespace-nowrap">
                    {uploadingDoc ? 'Uploading…' : '+ Choose Files'}
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      disabled={uploadingDoc}
                      accept={ALLOWED_ATTACHMENT_ACCEPT}
                      onChange={(e) => { handleFilesSelected(e.target.files); e.target.value = ''; }} />
                  </label>
                  <p className="font-['Metropolis'] text-[11px] text-[#51534A]">PDF · Word · Excel · Images · max 10 MB each</p>
                </div>
              </div>

              {attachments.length > 0 && (
                <div className="mt-3 space-y-2">
                  {attachments.map((att, idx) => (
                    <div key={idx} className="bg-white border border-line rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                      <div className="min-w-0 flex-1">
                        {/* Original filename + size sit small and muted —
                            still useful as a fallback / record of what
                            was uploaded. The big editable display-name
                            input sits below: that's what suppliers see
                            in the portal (display_name || file_name). */}
                        <p className="font-['Metropolis'] text-xs text-[#51534A] truncate">{att.file_name}</p>
                        <p className="font-['Metropolis'] text-[10px] text-[#51534A] mt-0.5">
                          {formatFileSize(att.file_size_bytes) || 'Attached'}
                          {!att.persisted && ' · save the brief to attach'}
                        </p>
                        <input
                          type="text"
                          value={att.display_name || ''}
                          onChange={e => updateAttachment(idx, { display_name: e.target.value })}
                          placeholder="Display name (optional — appears in place of the filename)"
                          className="mt-2 w-full bg-white border border-line rounded-xl text-ink px-3 py-2 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] transition-colors" />
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <a
                          href={att.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-white rounded-xl border border-[#2E8FA1] text-[#2E8FA1] font-['Metropolis'] text-xs font-semibold px-3 py-1.5 hover:bg-[#2E8FA1]/10 transition-colors">
                          View
                        </a>
                        <button
                          type="button"
                          onClick={() => removeAttachment(idx)}
                          disabled={uploadingDoc}
                          className="bg-white rounded-xl border border-[#E0290A] text-[#E0290A] font-['Metropolis'] text-xs font-semibold px-3 py-1.5 hover:bg-[#E0290A]/10 transition-colors disabled:opacity-50">
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p className={sectionClass}>Settings</p>
            <div className="space-y-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.is_privileged} onChange={e => set('is_privileged', e.target.checked)} className="w-4 h-4 accent-active-green" />
                <div>
                  <p className="font-['Metropolis'] text-sm text-fusion-mid-grey font-semibold">Privileged Brief</p>
                  <p className="font-['Metropolis'] text-xs text-[#51534A]">Only visible to Admins and Super Users</p>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.nda_required} onChange={e => set('nda_required', e.target.checked)} className="w-4 h-4 accent-active-green" />
                <div>
                  <p className="font-['Metropolis'] text-sm text-fusion-mid-grey font-semibold">NDA Required</p>
                  <p className="font-['Metropolis'] text-xs text-[#51534A]">Suppliers must sign an NDA before viewing this brief</p>
                </div>
              </label>
            </div>

            {/* "What the supplier sees" — per-field visibility controls
                paired with a live preview of the supplier facts panel.
                Visibility is independent of value: a filled field can
                still be hidden (e.g. Budget hidden to avoid anchoring
                quotes). Each row is only toggleable when its underlying
                value is filled in above; otherwise the row greys out
                with a "Not set" hint so the panel can't lie about what
                will actually render.

                Every brief fact is toggleable here. The only things NOT in
                this list are the always-on essentials (Response Deadline,
                the brief body, and attachments — hiding them would stop a
                supplier responding) and the internal-only flags (NDA,
                privileged) which are never supplier-facing. */}
            </section>
            )}

            {/* ══ PANEL 3 — Supplier view ══════════════════════════════════
                The visibility toggle list and the live preview. */}
            {activeTab === 'supplier-view' && (
            <section role="tabpanel" id="panel-supplier-view" aria-labelledby="tab-supplier-view" tabIndex={-1}>
            <p className="font-['Metropolis'] text-xs text-[#51534A] mb-4">
              Each fact only appears on the supplier's brief view when it has a value AND is ticked here. The preview on the right updates as you toggle.
            </p>
            {(() => {
              // Short dd/mm/yyyy formatter for the row displays.
              const fmtD = (iso) => {
                if (!iso) return null;
                const [y, m, d] = String(iso).slice(0, 10).split('-');
                return `${Number(d)}/${m}/${y}`;
              };
              const dateRange = (start, end) => {
                const s = fmtD(start);
                const e = fmtD(end);
                return s ? (e && e !== s ? `${s} – ${e}` : s) : null;
              };
              // Rig/de-rig carry a time — show it (unshifted) when present.
              const rigDateTime = (iso) => {
                if (!iso) return null;
                const [datePart, timePart] = String(iso).split('T');
                const [y, m, d] = datePart.slice(0, 10).split('-');
                if (!y || !m || !d) return null;
                const base = `${Number(d)}/${m}/${y}`;
                const hhmm = timePart ? timePart.slice(0, 5) : '';
                return (!hhmm || hhmm === '00:00') ? base : `${base} ${hhmm}`;
              };
              const rigDisplay = (required, start, end, details) => {
                if (!required) return null;
                const s = rigDateTime(start);
                const e = rigDateTime(end);
                const range = s ? (e && e !== s ? `${s} – ${e}` : s) : null;
                return range || (details && String(details).trim() ? 'Required' : null);
              };
              const ROWS = [
                {
                  key: 'show_project_number',
                  label: 'Project Number',
                  hasValue: !!(form.fusion_project_number && String(form.fusion_project_number).trim()),
                  display: form.fusion_project_number && String(form.fusion_project_number).trim(),
                },
                {
                  key: 'show_project_name',
                  label: 'Project Name',
                  hasValue: !!(form.fusion_project_name && String(form.fusion_project_name).trim()),
                  display: form.fusion_project_name && String(form.fusion_project_name).trim(),
                },
                {
                  key: 'show_client',
                  label: 'Client',
                  hasValue: !!(form.client && String(form.client).trim()),
                  display: form.client && String(form.client).trim(),
                },
                {
                  key: 'show_category',
                  label: 'Category',
                  hasValue: !!(form.brief_category && String(form.brief_category).trim()),
                  display: form.brief_category && String(form.brief_category).trim(),
                },
                {
                  key: 'show_event_date',
                  label: 'Event Date',
                  hasValue: !!(form.event_live_date && !form.event_live_date_na),
                  display: form.event_live_date && !form.event_live_date_na
                    ? dateRange(form.event_live_date, form.event_live_end_date_na ? null : form.event_live_end_date)
                    : null,
                },
                {
                  key: 'show_delivery_date',
                  label: 'Access',
                  hasValue: !!(form.expected_required_by_date && !form.delivery_date_na),
                  display: form.expected_required_by_date && !form.delivery_date_na
                    ? (form.delivery_access_to_date
                        ? `${fmtD(form.expected_required_by_date)} – ${fmtD(form.delivery_access_to_date)}`
                        : fmtD(form.expected_required_by_date))
                    : null,
                },
                {
                  key: 'show_delivery_address',
                  label: 'Delivery Address',
                  hasValue: !!(form.delivery_address && String(form.delivery_address).trim() && !form.delivery_date_na),
                  display: form.delivery_address && String(form.delivery_address).trim(),
                },
                {
                  key: 'show_location',
                  label: 'Location',
                  hasValue: !!(form.location && String(form.location).trim()),
                  display: form.location && String(form.location).trim(),
                },
                {
                  key: 'show_budget',
                  label: 'Budget',
                  // Range-aware display per 2026-06 budget-range
                  // migration. See fmtBudgetRange in lib/dates? — no,
                  // inlined here because the brief preview is the only
                  // builder surface that needs it; production display
                  // in BriefDetail / emails uses the same shape via
                  // fmtBudgetRange in src/lib/format.
                  hasValue: (form.minimum_budget != null && form.minimum_budget !== '') || (form.maximum_budget != null && form.maximum_budget !== ''),
                  display: (() => {
                    const hasMin = form.minimum_budget != null && form.minimum_budget !== '';
                    const hasMax = form.maximum_budget != null && form.maximum_budget !== '';
                    // Number formatting comes from the shared formatter; the
                    // "£X+" phrasing below is this preview's own and is left
                    // alone (lib/budget words the same case "From £X").
                    const gbp = (n) => formatGBP(n);
                    if (hasMin && hasMax) return `${gbp(form.minimum_budget)} – ${gbp(form.maximum_budget)}`;
                    if (hasMax)           return `Up to ${gbp(form.maximum_budget)}`;
                    if (hasMin)           return `${gbp(form.minimum_budget)}+`;
                    return null;
                  })(),
                },
                {
                  key: 'show_rig',
                  label: 'Rig',
                  hasValue: !!(form.rig_required && (form.rig_start_date || form.rig_end_date || (form.rig_details && String(form.rig_details).trim()))),
                  display: rigDisplay(form.rig_required, form.rig_start_date, form.rig_end_date, form.rig_details),
                },
                {
                  key: 'show_derig',
                  label: 'De-rig',
                  hasValue: !!(form.derig_required && (form.derig_start_date || form.derig_end_date || (form.derig_details && String(form.derig_details).trim()))),
                  display: rigDisplay(form.derig_required, form.derig_start_date, form.derig_end_date, form.derig_details),
                },
              ];
              // Brief Name is rendered ahead of the toggleable rows as a
              // non-interactive "always shown" reminder. The supplier
              // always sees the title (it's the brief detail page h1) so
              // there's no toggle to expose — but listing it here keeps
              // the controls column reading as a complete inventory of
              // what the supplier sees, rather than implicitly omitting
              // the most visible field.
              const briefNameValue = (form.brief_name || '').trim();
              return (
                <>
                {/* Visibility rework: a ticked-but-disabled checkbox
                    looked indistinguishable from "unticked" at a glance,
                    so the author couldn't scan what they'd chosen to
                    share vs hide. Three distinct row treatments now:
                      - Always-shown (Brief Name): lock icon + green
                        accent + "Always Shown" pill. Visually distinct
                        from greyed-out empty rows.
                      - Empty field (hasValue=false): no checkbox at all,
                        info icon + "Will show once filled in" copy.
                        Removes the "is this on or off?" ambiguity.
                      - Filled field (hasValue=true): real checkbox the
                        author drives. Shown / Hidden pill follows the
                        toggle state.
                    A "Hiding N field(s) with values" cue lives above the
                    grid as a quiet warning when the author has actively
                    hidden filled data — a guardrail against silent
                    accidents (eg. budget hidden by mistake). */}
                {(() => {
                  const hiddenFilled = ROWS.filter(r => r.hasValue && !form[r.key]);
                  if (hiddenFilled.length === 0) return null;
                  return (
                    <p className="font-['Metropolis'] text-[11px] text-[#7A4E10] mb-3 inline-flex items-center gap-1.5">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                      Hiding {hiddenFilled.length} field{hiddenFilled.length === 1 ? '' : 's'} that {hiddenFilled.length === 1 ? 'has' : 'have'} a value: {hiddenFilled.map(r => r.label).join(', ')}
                    </p>
                  );
                })()}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-2 items-start">
                  {/* Left — per-field controls */}
                  <div className="space-y-2">
                    <div
                      className="flex items-center gap-3 p-3 rounded-xl border bg-[#92D400]/[0.04] border-[#92D400]/40"
                      title="Brief Name is always shown to suppliers — it's the page title on their brief detail view."
                    >
                      {/* Lock icon — "this can't be toggled" reads cleaner
                          than a disabled checkbox. */}
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#92D400" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      <div className="flex-1 min-w-0">
                        <p className="font-['Metropolis'] text-sm font-semibold text-ink">Brief Name</p>
                        <p className="font-['Metropolis'] text-[11px] text-fusion-mid-grey truncate">
                          {briefNameValue || <span className="italic">Not set — add a name above</span>}
                        </p>
                      </div>
                      <span className={PILL.alwaysShown}>Always shown</span>
                    </div>
                    {ROWS.map(row => {
                      const isShown = form[row.key];
                      // Empty rows ditch the checkbox entirely so they
                      // can't be misread as "on" or "off". They render
                      // as a placeholder strip with info icon and copy
                      // that points back to the form fields above.
                      if (!row.hasValue) {
                        return (
                          <div
                            key={row.key}
                            className="flex items-center gap-3 p-3 rounded-xl border bg-white/40 border-line/60"
                            title={`${row.label} isn't filled in yet — once it's set you'll be able to toggle whether suppliers see it.`}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#51534A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0">
                              <circle cx="12" cy="12" r="10"/>
                              <line x1="12" y1="16" x2="12" y2="12"/>
                              <line x1="12" y1="8" x2="12.01" y2="8"/>
                            </svg>
                            <div className="flex-1 min-w-0">
                              <p className="font-['Metropolis'] text-sm font-semibold text-fusion-mid-grey">{row.label}</p>
                            </div>
                            {/* §8: this row carried an "Empty" pill AND an
                                italic "Set {field} above to show it" — the same
                                fact twice. The pill stays because every other
                                row in this list is pill-led, so keeping the
                                sentence here alone would make one row read
                                differently from its neighbours.
                                The sentence also went stale the moment the form
                                became tabbed: the field is no longer "above",
                                it's on the Brief information tab. */}
                            <span className={PILL.empty}>Empty</span>
                          </div>
                        );
                      }
                      const pill = isShown
                        ? { label: 'Shown', cls: PILL.shown }
                        : { label: 'Hidden', cls: PILL.hidden };
                      const rowBg = isShown
                        ? 'bg-[#92D400]/[0.04] border-[#92D400]/30'
                        : 'bg-white border-[#E89A2E]/30';
                      return (
                        <label
                          key={row.key}
                          className={`flex items-center gap-3 p-3 rounded-xl border transition-colors cursor-pointer ${rowBg}`}>
                          <input
                            type="checkbox"
                            checked={isShown}
                            onChange={e => set(row.key, e.target.checked)}
                            className="w-4 h-4 accent-active-green flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-['Metropolis'] text-sm font-semibold text-ink">{row.label}</p>
                            <p className="font-['Metropolis'] text-[11px] text-fusion-mid-grey truncate">{row.display}</p>
                          </div>
                          <span className={pill.cls}>{pill.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  {/* Right — live preview using the actual supplier
                      facts component so what staff see here matches the
                      supplier's view exactly.

                      §10: sticky. The toggle list on the left is long enough
                      that the preview scrolled out of view exactly when you
                      were toggling things to watch it change. `self-start` is
                      required — a grid item stretches to the row height by
                      default, and a full-height item has nothing to stick
                      within. Only sticky from lg up, where the two are
                      genuinely side by side. */}
                  <div className="lg:sticky lg:top-2 lg:self-start">
                    <p className="font-['Metropolis'] text-[11px] font-semibold text-[#51534A] mb-2">Live preview</p>
                    {/* Brief name — always shown to the supplier as the
                        page title above the facts panel. Mirrors the
                        SupplierResponse h1 styling so staff can see
                        exactly how their title reads (caps, weight,
                        letter-spacing) before the brief is sent. */}
                    <h3 className="font-['Metropolis'] text-xl font-extrabold tracking-[0.04em] uppercase text-ink leading-tight mb-3 break-words">
                      {form.brief_name && String(form.brief_name).trim()
                        ? form.brief_name
                        : <span className="text-[#51534A] italic font-semibold normal-case tracking-normal">Untitled brief — add a name above</span>}
                    </h3>
                    {(() => {
                      // Mirror save-time coherence: rig/de-rig + delivery
                      // address only persist when their parent toggle is on,
                      // so the preview clears them the same way.
                      const previewBrief = {
                        required_response_time: form.required_response_time || null,
                        fusion_project_number: form.fusion_project_number,
                        fusion_project_name: form.fusion_project_name,
                        client: form.client,
                        brief_category: form.brief_category,
                        event_live_date: form.event_live_date_na ? null : form.event_live_date,
                        event_live_end_date: form.event_live_end_date_na ? null : form.event_live_end_date,
                        expected_required_by_date: form.expected_required_by_date,
                        delivery_access_to_date: form.delivery_date_na ? null : form.delivery_access_to_date,
                        delivery_date_na: form.delivery_date_na,
                        delivery_address: form.delivery_date_na ? null : form.delivery_address,
                        location: form.location,
                        minimum_budget: form.minimum_budget,
                        maximum_budget: form.maximum_budget,
                        rig_start_date: form.rig_required ? form.rig_start_date : null,
                        rig_end_date: form.rig_required ? form.rig_end_date : null,
                        rig_details: form.rig_required ? form.rig_details : null,
                        derig_start_date: form.derig_required ? form.derig_start_date : null,
                        derig_end_date: form.derig_required ? form.derig_end_date : null,
                        derig_details: form.derig_required ? form.derig_details : null,
                        show_client: form.show_client,
                        show_event_date: form.show_event_date,
                        show_delivery_date: form.show_delivery_date,
                        show_budget: form.show_budget,
                        show_location: form.show_location,
                        show_project_name: form.show_project_name,
                        show_project_number: form.show_project_number,
                        show_category: form.show_category,
                        show_delivery_address: form.show_delivery_address,
                        show_rig: form.show_rig,
                        show_derig: form.show_derig,
                      };
                      // SupplierBriefFactsPanel returns null when no facts
                      // qualify; show an empty-state hint instead so staff
                      // aren't staring at a blank column. Use the same
                      // builder the panel uses so the check can't drift.
                      const fallback = (
                        <div className="border border-dashed border-line rounded-xl p-6 text-center">
                          <p className="font-['Metropolis'] text-xs text-[#51534A] italic">No facts will show yet — fill in fields above or toggle them on.</p>
                        </div>
                      );
                      return buildSupplierFacts(previewBrief).length > 0
                        ? <SupplierBriefFactsPanel brief={previewBrief} showWarnings narrow />
                        : fallback;
                    })()}
                  </div>
                </div>
                </>
              );
            })()}

            </section>
            )}

            {/* ══ PANEL 4 — Recipients ═════════════════════════════════════
                Brief type, then recipient selection.

                Brief Type BELONGS HERE, not with the visibility toggles. It
                describes who else is being asked, so it's a fact about the
                recipient list — and it was the specific cause of the reported
                bug: the Recipients anchor sat BELOW Brief Type, so opening
                that tab scrolled past it and showed Supplier-view rows above
                it instead. */}
            {activeTab === 'recipients' && (
            <section role="tabpanel" id="panel-recipients" aria-labelledby="tab-recipients" tabIndex={-1}>
            {/* Optional flag chosen by Fusion's intent. Three states;
                "Don't indicate" is a first-class default that preserves legacy
                behaviour on existing briefs. The exact recipient count is
                never surfaced to suppliers — see the info hint below. */}
            <p className={sectionClass}>Brief Type</p>
            <p className="font-['Metropolis'] text-xs text-[#51534A] mb-4">
              Indicate the nature of this brief to suppliers. Letting them know whether they're the only one being asked, or one of several, helps them respond appropriately. Optional — leave unset if you don't want to indicate either way.
            </p>
            {(() => {
              const TYPE_OPTIONS = [
                {
                  value: null,
                  label: "Don't indicate",
                  description: "Suppliers won't see any indicator of the brief's nature. Default behaviour.",
                  accent: null,
                },
                {
                  value: 'exclusive',
                  label: 'Exclusive',
                  description: "This brief is being sent to a single supplier only. They'll see an 'Exclusive Brief' badge.",
                  accent: '#009159',
                },
                {
                  value: 'competitive',
                  label: 'Competitive',
                  description: "This brief is being sent to multiple suppliers. They'll see a 'Competitive Brief' badge.",
                  accent: '#2E8FA1',
                },
              ];
              return (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    {TYPE_OPTIONS.map(opt => {
                      const isSelected = (form.brief_type || null) === opt.value;
                      const accent = opt.accent || '#92D400';
                      const borderColor = isSelected ? accent : '#DDE1D0';
                      const bgTint = isSelected && opt.accent
                        ? `${opt.accent}14`
                        : isSelected
                          ? 'rgba(146,212,0,0.06)'
                          : '#F7F9F0';
                      return (
                        <label
                          key={opt.value || 'unset'}
                          className="flex items-start gap-3 p-4 rounded-xl cursor-pointer transition-colors"
                          style={{ background: bgTint, border: `1px solid ${borderColor}` }}
                        >
                          <input
                            type="radio"
                            name="brief_type"
                            checked={isSelected}
                            onChange={() => set('brief_type', opt.value)}
                            className="mt-0.5 w-4 h-4 flex-shrink-0"
                            style={{ accentColor: accent }}
                          />
                          <div className="min-w-0">
                            {/* §6: unselected titles were #F9FCF1 — near-white
                                on a #F7F9F0 near-white card, so the word naming
                                the option was the least readable text on it
                                while its description sat at normal contrast.
                                The title is now full-contrast ink in BOTH
                                states; selection is carried by the border, the
                                background tint and the radio, which is where it
                                belonged. */}
                            <p
                              className="font-['Metropolis'] text-sm font-bold tracking-[0.04em] text-ink"
                            >
                              {opt.label}
                            </p>
                            <p className="font-['Metropolis'] text-[11px] text-[#51534A] mt-1 leading-relaxed">
                              {opt.description}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  <div
                    className="flex items-start gap-2.5 rounded-xl px-4 py-3 mb-4"
                    style={{ background: 'rgba(46,143,161,0.06)', border: '1px solid rgba(46,143,161,0.25)' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2E8FA1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0 mt-0.5">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                    <p className="font-['Metropolis'] text-xs text-fusion-mid-grey leading-relaxed">
                      <strong className="text-ink">The exact supplier count is never shown to suppliers</strong> — they'll only know the type. Fusion always sees the count on the Recipients &amp; Responses panel.
                    </p>
                  </div>
                </>
              );
            })()}

            {/* Recipients section - two column layout */}
            <p className={sectionClass}>Select Recipients</p>
            <p className="font-['Metropolis'] text-xs text-[#51534A] mb-4">Recipients will not be able to see each other — each supplier only sees their own invitation.</p>

            {/* Counter strip — Selected counts always; Accepted /
                Declined only render once the brief has been sent (in
                edit mode after send, or any time a recipient already
                carries a status). Pre-send both counters are stuck at
                0 — meaningless noise that hides the only number that
                actually matters during creation. */}
            {(() => {
              const acceptedCount = selectedSuppliers.filter(s => s.status === 'responded').length;
              const declinedCount = selectedSuppliers.filter(s => s.status === 'declined').length;
              const showResponseCounters = isEditMode && (acceptedCount > 0 || declinedCount > 0);
              // §12 — nothing selected and nothing to count means the bar is a
              // white box announcing a zero. The dashed empty state further
              // down already says nobody has been added and what to do about
              // it, so this was the second thing saying so, and the louder of
              // the two. It appears the moment there is something to report.
              if (selectedSuppliers.length === 0 && !showResponseCounters) return null;
              return (
                // §6: was a full-width white box wrapping a two-digit number,
                // which gave a count more visual weight than the suppliers it
                // was counting. Slim bar, and the selection itself is shown as
                // removable chips — you can see and undo who's on the brief
                // without hunting through the tiles below.
                <div className="bg-white border border-line rounded-xl px-4 py-2.5 mb-5">
                  <div className="flex items-center gap-4 flex-wrap">
                    <p className="font-['Metropolis'] text-sm text-ink">
                      <span className="font-bold">{selectedSuppliers.length}</span>
                      {' '}supplier{selectedSuppliers.length === 1 ? '' : 's'} selected
                    </p>
                    {showResponseCounters && (
                      <>
                        <span aria-hidden="true" className="text-[#D3D8C6]">·</span>
                        <p className="font-['Metropolis'] text-sm text-fusion-forest">
                          <span className="font-bold">{acceptedCount}</span> accepted
                        </p>
                        <p className="font-['Metropolis'] text-sm text-[#E0290A]">
                          <span className="font-bold">{declinedCount}</span> declined
                        </p>
                      </>
                    )}
                  </div>
                  {selectedSuppliers.length > 0 && (
                    <ul className="flex items-center gap-1.5 flex-wrap mt-2.5">
                      {selectedSuppliers.map(s => (
                        <li key={s.id}>
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-fusion-forest/[0.07] border border-fusion-forest/20 pl-2.5 pr-1 py-0.5">
                            <span className="font-['Metropolis'] text-xs text-fusion-forest">{s.supplier_name}</span>
                            <button
                              type="button"
                              onClick={() => removeSelectedSupplier(s.id)}
                              aria-label={`Remove ${s.supplier_name}`}
                              className="w-4 h-4 rounded-full inline-flex items-center justify-center text-fusion-forest/70 hover:text-white hover:bg-fusion-forest transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-fusion-forest">
                              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })()}

            {/* Filter toolbar (§7). Category, tags and search were three
                separate containers stacked down the page — three boxes to do
                one job. One row now, stacking on narrow viewports. The tag
                filter reuses the directory's TagFilterBar: "+ Add filter"
                → searchable popover, chips, AND semantics, live match count. */}
            <div className="bg-white border border-line rounded-xl px-4 py-3 mb-4">
              <div className="flex items-end gap-3 flex-wrap">
                <div className="min-w-[180px] flex-1">
                  <label htmlFor="recipient-category" className={labelClass}>Category</label>
                  <select id="recipient-category" className={inputClass} value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                    <option value="">All categories</option>
                    {categories.map(c => <option key={c.value} value={c.value}>{c.value}</option>)}
                  </select>
                </div>
                <div className="min-w-[220px] flex-[2]">
                  <label htmlFor="recipient-search" className={labelClass}>Search</label>
                  <input id="recipient-search" type="text" value={supplierSearch} onChange={e => setSupplierSearch(e.target.value)} placeholder="Search by name or category…" className={inputClass} />
                </div>
              </div>
              <div className="mt-2.5">
                {/* §10: `bare` — this sits inside the filter card alongside
                    Category and Search, which have no box of their own, so the
                    default card chrome drew a border inside a border. */}
                <TagFilterBar
                  vocabulary="supplier"
                  selectedIds={recipientTagFilter}
                  onChange={setRecipientTagFilter}
                  matchCount={filteredSuppliers.length}
                  noun="supplier"
                  bare
                />
              </div>
            </div>

            <div className={`grid grid-cols-2 ${fieldGap}`}>

              {/* Left — supplier list */}
              <div>
                <div className="border border-line rounded-xl max-h-96 overflow-y-auto">
                  {filteredSuppliers.length === 0 ? (
                    <p className="font-['Metropolis'] text-sm text-[#51534A] p-4">No suppliers match these filters.</p>
                  ) : filteredSuppliers.map(supplier => {
                    const isSelected = selectedSuppliers.find(s => s.id === supplier.id);
                    const ndaMissing = form.nda_required && !validNdaSupplierIds.has(supplier.id);
                    const noPortalUsers = (portalUsersBySupplier.get(supplier.id) || []).length === 0;
                    // A supplier with no portal user can't actually be
                    // notified, so SELECTING them is a dead end (the
                    // brief sends but they receive nothing). Block the
                    // click + grey the row out + disable the checkbox.
                    // Already-selected rows stay clickable so the user
                    // can deselect a previously-picked supplier whose
                    // portal user has since lapsed.
                    const blockSelection = noPortalUsers && !isSelected;
                    return (
                      <div
                        key={supplier.id}
                        onClick={() => { if (!blockSelection) toggleSupplier(supplier); }}
                        title={blockSelection ? "No portal user on file — add one on the supplier page before notifying" : undefined}
                        className={`flex items-center gap-3 px-4 py-3 border-b border-line last:border-0 transition-colors ${isSelected ? 'bg-[#92D400]/10' : 'hover:bg-white'} ${(ndaMissing || noPortalUsers) && !isSelected ? 'opacity-50' : ''} ${blockSelection ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <input
                          type="checkbox"
                          checked={!!isSelected}
                          onChange={() => {}}
                          disabled={blockSelection}
                          className={`w-4 h-4 accent-active-green ${blockSelection ? 'cursor-not-allowed' : ''}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-['Metropolis'] text-sm text-ink font-semibold truncate">{supplier.supplier_name}</p>
                            {ndaMissing && (
                              <span className={PILL.hidden}>NDA missing</span>
                            )}
                            {/* §15: no NO PORTAL USER badge here. The same
                                supplier carried both this and the inline
                                "⚠ No portal user — can't notify" line below;
                                the inline one explains the consequence, so
                                that's the one that stays. */}
                          </div>
                          {/* §15: omit the line entirely when there's no
                              category, rather than printing "Not set" under
                              every supplier. The supplier RECORD still says
                              "Not set" — there, it's a fact about the record
                              you might act on. In a picker list it's just the
                              same placeholder repeated down the page. */}
                          {categoryLabel(supplier, { fallback: null }) && (
                            <p className="font-['Metropolis'] text-xs text-[#51534A]">{categoryLabel(supplier, { fallback: null })}</p>
                          )}
                          {(() => {
                            const tlist = tagsBySupplier.get(supplier.id) || [];
                            if (tlist.length === 0) return null;
                            return (
                              <div className="flex flex-wrap items-center gap-1 mt-1">
                                {tlist.slice(0, 4).map(t => <TagChip key={t.id} tag={t} />)}
                                {tlist.length > 4 && <span className="font-['Metropolis'] text-[10px] font-bold text-[#51534A]">+{tlist.length - 4}</span>}
                              </div>
                            );
                          })()}
                          {(() => {
                            const liveUsers = portalUsersBySupplier.get(supplier.id) || [];
                            if (liveUsers.length === 0) {
                              return <p className="font-['Metropolis'] text-xs text-[#E0290A]">⚠ No portal user — can't notify</p>;
                            }
                            const first = liveUsers[0].full_name || liveUsers[0].email;
                            return (
                              <p className="font-['Metropolis'] text-xs text-fusion-mid-grey truncate">
                                {first}{liveUsers.length > 1 ? ` · +${liveUsers.length - 1} more` : ''}
                              </p>
                            );
                          })()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right — selected supplier tiles. Counters previously
                  lived inside this column; lifted out to the full-width
                  strip above so the two columns share a baseline. */}
              <div>
                {/* Tiles */}
                {/* Empty state: an empty screen is an invitation to act (§16).
                    Was a 384px box of tracked grey caps — the height implied
                    content was missing rather than waiting to be chosen. */}
                {selectedSuppliers.length === 0 ? (
                  <div className="border border-dashed border-[#DDE2CE] rounded-xl py-10 px-6 flex flex-col items-center justify-center text-center gap-2">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#8B9078" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <line x1="19" y1="8" x2="19" y2="14" />
                      <line x1="22" y1="11" x2="16" y2="11" />
                    </svg>
                    <p className="font-['Metropolis'] text-sm text-[#51534A]">Choose suppliers from the list on the left to add them to this brief.</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                    {selectedSuppliers.map(supplier => {
                      const ndaMissing = form.nda_required && !validNdaSupplierIds.has(supplier.id);
                      return (
                      <div key={supplier.id} className={`border-2 ${tileStyle(supplier.status)} bg-white p-4`}>

                        {/* Tile header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3 flex-wrap">
                            <p className={`font-['Metropolis'] text-sm font-semibold ${nameStyle(supplier.status)}`}>
                              {supplier.supplier_name}
                            </p>
                            <StatusBadge status={supplier.status} />
                            {ndaMissing && (
                              <span className={PILL.hidden}>NDA missing — remove</span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeSelectedSupplier(supplier.id)}
                            className="font-['Metropolis'] text-xs font-medium text-[#51534A] hover:text-[#E0290A] transition-colors"
                          >
                            Remove
                          </button>
                        </div>

                        {/* Portal Users — the single source of brief comms.
                            Each row is a checkbox per active portal user; all
                            on by default, uncheck to exclude for this brief. */}
                        {supplier.contacts.length > 0 && (
                          <div className="mb-3">
                            <p className="font-['Metropolis'] text-[11px] font-semibold text-[#51534A] mb-2">Portal users</p>
                            {supplier.contacts.map(contact => (
                              <label key={contact.email} className="flex items-start gap-2 mb-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={contact.selected}
                                  onChange={() => toggleContact(supplier.id, contact.email)}
                                  className="w-3 h-3 accent-active-green mt-1 flex-shrink-0"
                                />
                                <span className="font-['Metropolis'] text-xs text-fusion-mid-grey min-w-0">
                                  <span className="font-semibold">{contact.name || '—'}</span>
                                  <span className="text-[#51534A]"> · {contact.email}</span>
                                </span>
                              </label>
                            ))}
                            {supplier.contacts.length > 0 && supplier.contacts.every(c => !c.selected) && (
                              <p className="font-['Metropolis'] text-xs text-[#E0290A] mt-2">⚠ At least one portal user must be selected.</p>
                            )}
                          </div>
                        )}

                        {supplier.contacts.length === 0 && (
                          <p className="font-['Metropolis'] text-xs text-[#E0290A] mb-3">⚠ No active portal user — add one on the supplier page before sending.</p>
                        )}

                        {/* Additional emails */}
                        <div className="mb-3">
                          <p className="font-['Metropolis'] text-[11px] font-semibold text-[#51534A] mb-1">Additional emails</p>
                          <input
                            type="text"
                            value={supplier.additional_emails}
                            onChange={e => updateSupplierField(supplier.id, 'additional_emails', e.target.value)}
                            placeholder="email1@co.com; email2@co.com"
                            className="w-full bg-white border border-line rounded-xl text-ink px-3 py-1.5 font-['Metropolis'] text-xs focus:outline-none focus:border-[#92D400] transition-colors"
                          />
                        </div>

                        {/* Individual deadline */}
                        <div>
                          <p className="font-['Metropolis'] text-[11px] font-semibold text-[#51534A] mb-1">Response deadline</p>
                          <DatePicker
                            selected={toDate(supplier.response_deadline)}
                            onChange={(d) => updateSupplierField(supplier.id, 'response_deadline', fromDate(d))}
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            dateFormat="dd/MM/yyyy HH:mm"
                            placeholderText="dd/mm/yyyy --:--"
                            isClearable
                            className="w-full bg-white border border-line rounded-xl text-ink px-3 py-1.5 font-['Metropolis'] text-xs focus:outline-none focus:border-[#92D400] transition-colors"
                            wrapperClassName="block w-full" />
                        </div>

                      </div>
                    );
                    })}
                  </div>
                )}
              </div>
            </div>

            </section>
            )}

          </div>
        </div>
      </div>

      {/* Exclusive-brief multi-select confirmation (2026-06): renders
          at the root so the backdrop covers the whole page. Replaces
          the original window.confirm with the brand-styled
          ConfirmDialog. */}
      <ConfirmDialog
        open={!!exclusiveConfirmSupplier}
        title="Add to an exclusive brief?"
        body={
          exclusiveConfirmSupplier
            ? `This brief is set as Exclusive — exclusive briefs usually go to one supplier only. You already have ${selectedSuppliers.length} selected. Add ${exclusiveConfirmSupplier.supplier_name} anyway?`
            : ''
        }
        confirmLabel="Add anyway"
        cancelLabel="Keep exclusive"
        tone="primary"
        onConfirm={() => {
          const sup = exclusiveConfirmSupplier;
          setExclusiveConfirmSupplier(null);
          if (sup) toggleSupplier(sup, { skipExclusiveCheck: true });
        }}
        onCancel={() => setExclusiveConfirmSupplier(null)}
      />
    </div>
  );
}
